import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './AddNewProduct.css';
import CategorySelector from './CategorySelector';
import ProductDescriptionHtmlEditor, { DESCRIPTION_FORMAT } from './ProductDescriptionHtmlEditor';
import useCreateProduct from '../hooks/useCreateProduct';
import useUpdateProduct from '../hooks/useUpdateProduct';
import useProducts from '../hooks/useProducts';
import { generateAutoSKU, generateSKUPreview, generateProductIdFromSKU } from '../utils/skuGenerator';
import { buildProductSeoSlug, resolveCreatorSlugForAdminUser } from '../../utils/productSeoUrl';
// ✅ Admin authentication requirement removed as per requirement
import { productService, categoryService } from '../../services/api';
import {
  normalizeHexColor,
  getColorDisplayName,
  isPlaceholderColorKey,
  isPlaceholderSizeKey,
  extractHexPaletteFromColorNames,
} from '../../utils/productColorDisplay';
import {
  PRESET_PRODUCT_COLORS,
  getSizePresetsForMainCategoryName,
  VARIANT_COLOR_SEARCH_EXTRAS,
  mergeSearchableColorOptions,
  filterColorsByQuery,
} from '../constants/productVariantPresets';
import {
  getMissingFieldsForCreate,
  getMissingFieldsForUpdate,
  formatMissingFieldsMessage,
} from '../utils/productFormValidation';
import {
  readProductStatusFlags,
  buildProductStatusSyncPayload,
  syncProductStatusFlagsAfterSave,
} from '../utils/productStatusFlags';
import { canSetProductFeatured } from '../constants/teamAccessPermissions';
import { getApiUrl } from '../../config/environment';
import { getAdminToken } from '../utils/authUtils';
import {
  getMongoProductId,
  isMongoObjectId,
  extractCreatedProductId,
} from '../utils/productMongoId';
import {
  resolveProductCategorySelection,
  mapImagesForProductApi,
} from '../utils/resolveProductCategories';
import {
  buildProductPricingFields,
  pricesFromStoredProduct,
  normalizeProductPricesForDisplay,
} from '../utils/productPricing';
import { EMPTY_NEW_PRODUCT } from '../utils/newProductFormDefaults';
import {
  blocksToLegacyHtml,
  mergeDescriptionRichSources,
  hasRenderableRichContent,
  stripEmbedFromDescriptionForDisplay,
} from '../../utils/productDescriptionRich';
import {
  descriptionPlainForValidation,
  isLikelyHtmlDescription,
  stripHtmlToPlain,
} from '../../utils/productDescriptionHtml';
import { extractMaterialTagsFromDescription } from '../utils/extractMaterialsFromDescription';
import { sanitizeProductDescriptionHtml } from '../../utils/sanitizeProductHtml';
import {
  ADD_PRODUCT_WIZARD_STEPS,
  isAddProductWizardStepComplete,
  canShowWizardNextButton,
  getAddProductWizardStepMissingFields,
  formatWizardStepMissingMessage,
} from '../utils/addProductWizard';
import { getPrimaryProductImageUrl } from '../../utils/productImages';
import {
  getPointerClientCoords,
  sampleHexFromImageElement,
  prefersPhotoColorPicker,
} from '../utils/sampleColorFromImage';

function resolveSuccessPreviewImageUrl(product, uploadedImagesList) {
  const fromProduct = getPrimaryProductImageUrl(product);
  if (fromProduct) return fromProduct;
  const list = Array.isArray(uploadedImagesList) ? uploadedImagesList : [];
  const primary = list.find((i) => i?.isPrimary) || list.find((i) => i?.url || i?.previewUrl) || list[0];
  if (!primary) return '';
  return primary.previewUrl || primary.url || primary.cloudinaryUrl || '';
}

function resolveSuccessPreviewCategoryLabel(product, formProduct) {
  if (typeof product?.category === 'object' && product.category?.name) {
    return String(product.category.name).trim();
  }
  const isId = (v) => /^[a-f0-9]{24}$/i.test(String(v || '').trim());
  const main = String(formProduct?.mainCategory || product?.mainCategory || '').trim();
  const sub = String(formProduct?.subcategory || product?.subcategory || '').trim();
  const child = String(formProduct?.childrenCategory || product?.childrenCategory || '').trim();
  const parts = [main, sub, child].filter((p) => p && !isId(p));
  if (parts.length) return parts.join(' › ');
  const rawCat = product?.category;
  if (typeof rawCat === 'string' && rawCat.trim() && !/^[a-f0-9]{24}$/i.test(rawCat.trim())) {
    return rawCat.trim();
  }
  return 'Category';
}

function buildPublicProductPageUrl(product) {
  if (!product || typeof product !== 'object') return '';
  const slug = String(product.url || product.slug || product.productUrl || '').trim();
  if (slug.startsWith('http://') || slug.startsWith('https://')) return slug;
  if (slug) {
    const path = slug.startsWith('/') ? slug : `/${slug}`;
    return `${window.location.origin}${path}`;
  }
  const productId = product.id || product._id || product.productId;
  return productId ? `${window.location.origin}/product/${productId}` : '';
}

function buildGoldenSuccessProductState(resultProduct, formProduct, uploadedImagesList, pricedForDisplay) {
  const images =
    Array.isArray(resultProduct?.images) && resultProduct.images.length > 0
      ? resultProduct.images
      : mapImagesForProductApi(uploadedImagesList || []);
  const merged = {
    ...(resultProduct && typeof resultProduct === 'object' ? resultProduct : {}),
    ...pricedForDisplay,
    images,
    mainCategory: formProduct?.mainCategory || resultProduct?.mainCategory || '',
  };
  return {
    ...merged,
    _previewImageUrl: resolveSuccessPreviewImageUrl(merged, uploadedImagesList),
    _previewCategoryLabel: resolveSuccessPreviewCategoryLabel(merged, formProduct),
    _previewPublicUrl: buildPublicProductPageUrl({ ...merged, url: merged.url || formProduct?.url }),
  };
}

function escapePlainForHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function plainLinesToHtml(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return '<p><br></p>';
  return lines.map((line) => `<p>${escapePlainForHtml(line)}</p>`).join('');
}

/** Drop size columns from nested stock when sizes are removed from the product. */
function pruneStockRemovedSizes(stock, removedSizeLabels) {
  if (!removedSizeLabels.length || !stock || typeof stock !== 'object') return stock;
  const rem = new Set(removedSizeLabels);
  const next = { ...stock };
  for (const ck of Object.keys(next)) {
    const row = next[ck];
    if (!row || typeof row !== 'object') continue;
    const nr = { ...row };
    for (const s of rem) {
      delete nr[s];
    }
    next[ck] = nr;
  }
  return next;
}

/** Optional product showcase video (must match client validation in useCreateProduct). */
const MAX_PRODUCT_VIDEO_BYTES = 5 * 1024 * 1024;
const PRODUCT_VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-msvideo,.mp4,.webm,.mov';

/** One-click material / style tags (saved in product.tags like manual entries). */
const MATERIAL_QUICK_TAGS = [
  '1 Piece',
  '2 Piece',
  '3 Piece',
  'Shirt',
  'Trouser',
  'Kurti',
  'Dupatta',
  'Unstitched',
  'Embroidery',
  'Lawn',
  'Cotton',
  'Silk',
  'Velvet',
  'Net',
  'Linen',
  'Chiffon',
  'Formal',
  'Casual',
  'Festive',
  'Winter',
  'Summer',
  'Shalwar Kameez',
  'Abaya',
  'Pret',
  'Stitched',
  'Traditional',
];

const MATERIAL_QUICK_TAG_SET = new Set(MATERIAL_QUICK_TAGS.map((t) => t.toLowerCase()));

/** Single-cell stock when no color/size matrix; must align with cart / bulk stock `default` keys. */
const SIMPLE_STOCK_COLOR = 'default';
const SIMPLE_STOCK_SIZE = 'default';

function aggregateNestedStock(stock) {
  if (!stock || typeof stock !== 'object') return 0;
  let sum = 0;
  const walk = (node) => {
    if (typeof node === 'number' && !Number.isNaN(node)) sum += node;
    else if (typeof node === 'string' && node.trim() !== '') {
      const n = parseInt(node, 10);
      if (!Number.isNaN(n)) sum += n;
    } else if (node && typeof node === 'object') Object.values(node).forEach(walk);
  };
  walk(stock);
  return sum;
}

/** Aligns with storefront `getLinkedColorFromImage` — admin load must preserve links for edit / Photos hub. */
function getLinkedColorFromImageAdmin(image) {
  if (!image || typeof image !== 'object') return '';
  const v =
    image.linkedColor ??
    image.linked_color ??
    image.variantColor ??
    image.variant_color;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

/** Include variant keys that exist only on `images[].linkedColor` so chips & stock match photo links. */
function mergeVariantColorsFromImageLinks(colorsArray, formattedImages) {
  const out = Array.isArray(colorsArray) ? [...colorsArray] : [];
  const seen = new Set();
  for (const c of out) {
    const t = String(c ?? '').trim();
    if (!t) continue;
    const h = normalizeHexColor(t);
    seen.add(h ? h : t.toLowerCase());
  }
  for (const img of formattedImages || []) {
    const lc = img?.linkedColor;
    if (!lc) continue;
    const t = String(lc).trim();
    if (!t) continue;
    const h = normalizeHexColor(t);
    const key = h ? h : t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function applyLinkedColorDraftStateFromImages(formattedImages, setHexDraftByImage, setImageColorInput) {
  const hexInit = {};
  const imgInputInit = {};
  (formattedImages || []).forEach((img, i) => {
    const lc = img?.linkedColor;
    if (!lc) return;
    const s = String(lc).trim();
    imgInputInit[i] = s;
    const hx = normalizeHexColor(s);
    if (hx) hexInit[i] = hx;
  });
  setHexDraftByImage(hexInit);
  setImageColorInput(imgInputInit);
}

const AddNewProduct = ({
  onNavigate,
  editingProduct,
  onEditComplete,
  currentUser,
  variantHubOnly = false,
}) => {
  const useProductWizard = !variantHubOnly;
  const allowFeaturedProduct = canSetProductFeatured(currentUser);
  const [wizardStep, setWizardStep] = useState(0);
  /** Stock qty placeholders (e.g. "Minimum 1") hide on focus, not only after typing */
  const [stockPlaceholderSuppressed, setStockPlaceholderSuppressed] = useState({});
  const suppressStockPlaceholder = (key) => {
    setStockPlaceholderSuppressed((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };
  const restoreStockPlaceholder = (key) => {
    setStockPlaceholderSuppressed((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };
  const stockQtyPlaceholder = (key, label) => (stockPlaceholderSuppressed[key] ? '' : label);
  const [newProduct, setNewProduct] = useState(() => ({ ...EMPTY_NEW_PRODUCT }));

  const [uploadedImages, setUploadedImages] = useState([]);
  /** { file, previewUrl, name, size } | null — new video chosen for upload */
  const [pendingVideo, setPendingVideo] = useState(null);
  /** Existing video URL from API when editing (no new file selected). */
  const [existingVideoUrl, setExistingVideoUrl] = useState('');
  const [descriptionFormat, setDescriptionFormat] = useState(DESCRIPTION_FORMAT.HTML);
  const [descLoadNonce, setDescLoadNonce] = useState(0);
  const descriptionEditorKey = useMemo(
    () => `${editingProduct?.id || editingProduct?._id || editingProduct?.productId || 'new'}-${descLoadNonce}`,
    [editingProduct?.id, editingProduct?._id, editingProduct?.productId, descLoadNonce]
  );
  const [customColorPalette, setCustomColorPalette] = useState([]);

  const fullPalette = useMemo(() => {
    const presetNames = new Set(PRESET_PRODUCT_COLORS.map((c) => c.name));
    const merged = [...PRESET_PRODUCT_COLORS];
    customColorPalette.forEach((c) => {
      if (!presetNames.has(c.name)) {
        presetNames.add(c.name);
        merged.push(c);
      }
    });
    return merged;
  }, [customColorPalette]);

  const getColorCode = useCallback(
    (colorName) => {
      const hex = normalizeHexColor(String(colorName));
      if (hex) return hex;
      const p = fullPalette.find((c) => c.name === colorName);
      return p?.code || '#ccc';
    },
    [fullPalette]
  );

  /** Register a variant color name or hex on the product (palette + newProduct.colors). */
  const addQuickVariantColor = useCallback((colorKey) => {
    const trimmed = colorKey && String(colorKey).trim();
    if (!trimmed) return;
    const hex = normalizeHexColor(trimmed);
    if (hex) {
      setCustomColorPalette((p) =>
        p.some((c) => c.name === hex) ? p : [...p, { name: hex, code: hex }]
      );
    }
    setNewProduct((prev) => {
      if (prev.colors.includes(trimmed)) return prev;
      return { ...prev, colors: [...prev.colors, trimmed] };
    });
  }, []);

  /** Per-image hex draft for "Link hex to this photo" on each card */
  const [hexDraftByImage, setHexDraftByImage] = useState({});
  /** Type-to-search variant color per image index */
  const [imageColorInput, setImageColorInput] = useState({});
  const [openImageColorSuggest, setOpenImageColorSuggest] = useState(null);
  const imageColorBlurTimerRef = useRef(null);
  /** Wizard step 3 — add variant colors without linking a photo */
  const [globalQuickColorInput, setGlobalQuickColorInput] = useState('');
  const [openGlobalQuickColorSuggest, setOpenGlobalQuickColorSuggest] = useState(false);
  const [globalHexDraft, setGlobalHexDraft] = useState('#888888');
  const globalQuickColorBlurTimerRef = useRef(null);
  /** Mobile / no EyeDropper: tap a product photo to sample hex — { scope: 'global' } | { scope: 'image', index } */
  const [photoColorPick, setPhotoColorPick] = useState(null);
  const searchableColorsForImageLink = useMemo(
    () =>
      mergeSearchableColorOptions(
        PRESET_PRODUCT_COLORS,
        VARIANT_COLOR_SEARCH_EXTRAS,
        customColorPalette
      ),
    [customColorPalette]
  );

  const setImageLinkedColor = useCallback((index, colorKey) => {
    const trimmed = colorKey && String(colorKey).trim();
    setUploadedImages((prev) =>
      prev.map((img, i) =>
        i === index ? { ...img, linkedColor: trimmed || undefined } : img
      )
    );
    if (!trimmed) return;
    addQuickVariantColor(trimmed);
  }, [addQuickVariantColor]);

  const [saved, setSaved] = useState(false);
  const productVideoInputRef = useRef(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [showGoldenSuccess, setShowGoldenSuccess] = useState(false);
  const [newProductData, setNewProductData] = useState(null);
  const [bypassValidation, setBypassValidation] = useState(false);
  const [bypassStockValidation, setBypassStockValidation] = useState(false);
  // Removed uploadingImages state since we're not uploading immediately
  const [skuPreview, setSkuPreview] = useState('');
  const [autoGenerateSKU, setAutoGenerateSKU] = useState(true);
  
  // Subcategories for material suggestions (values still stored in product.tags)
  const [availableSubcategories, setAvailableSubcategories] = useState([]);
  const [tagsInput, setTagsInput] = useState(''); // Single tag input (not comma-separated)
  const [showSubcategorySuggestions, setShowSubcategorySuggestions] = useState(false);
  
  // ✅ Authentication state removed - no longer required

  // Use the new hooks
  const { createProduct, loading: creating } = useCreateProduct();
  const { updateProduct, updateProductPricing, loading: updating } = useUpdateProduct();
  const { uploadImage, uploadMultipleImages, products, refetch: refetchProducts } = useProducts();

  const existingBrandsFromCatalog = useMemo(() => {
    if (!Array.isArray(products)) return [];
    const seen = new Map();
    for (const p of products) {
      const raw = p?.brand;
      const b = typeof raw === 'string' ? raw.trim() : '';
      if (!b) continue;
      const key = b.toLowerCase();
      if (!seen.has(key)) seen.set(key, b);
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [products]);

  /** Tags already used on other products (excluding preset quick-select list to avoid duplicate chips). */
  const existingMaterialTagsFromCatalog = useMemo(() => {
    if (!Array.isArray(products)) return [];
    const seen = new Map();
    for (const p of products) {
      const tags = p?.tags;
      if (!Array.isArray(tags)) continue;
      for (const t of tags) {
        const s = typeof t === 'string' ? t.trim() : String(t || '').trim();
        if (!s) continue;
        const key = s.toLowerCase();
        if (MATERIAL_QUICK_TAG_SET.has(key)) continue;
        if (!seen.has(key)) seen.set(key, s);
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [products]);

  const descriptionPlainForMaterials = useMemo(
    () => descriptionPlainForValidation(newProduct.description),
    [newProduct.description]
  );

  const aiMaterialSuggestions = useMemo(
    () => extractMaterialTagsFromDescription(newProduct.description, MATERIAL_QUICK_TAGS),
    [newProduct.description]
  );

  // Debug: Log editing product when component mounts or editingProduct changes
  useEffect(() => {
    console.log('🔍 AddNewProduct - editingProduct changed:', editingProduct);
    if (editingProduct) {
      console.log('🔍 Editing product ID fields:', {
        id: editingProduct.id,
        _id: editingProduct._id,
        productId: editingProduct.productId,
        allKeys: Object.keys(editingProduct)
      });
      console.log('🔍 Edit mode should be active - editingProduct exists');
    } else {
      console.log('🔍 Edit mode not active - editingProduct is null/undefined');
    }
  }, [editingProduct]);

  // ✅ Authentication check removed - no longer required

  // Generate initial SKU when component mounts (only for new products)
  useEffect(() => {
    if (!editingProduct && autoGenerateSKU && newProduct.name) {
      generateNewSKU(newProduct.name);
    }
  }, [editingProduct, autoGenerateSKU]);

  // Regenerate SKU when product name changes (only for new products)
  useEffect(() => {
    if (!editingProduct && autoGenerateSKU && newProduct.name) {
      generateNewSKU(newProduct.name);
    }
  }, [newProduct.name]);

  const mainCategories = ['Fashion', 'Accessories', 'Footwear', 'Beauty'];
  
  console.log('🔄 AddNewProduct Component Loaded - Main Categories:', mainCategories);
  
  // Helper function to validate MongoDB ObjectId
  const isValidObjectId = (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  };
  
  // Temporary alert to show component has loaded with changes
  useEffect(() => {
    console.log('✅ AddNewProduct Component Updated - Changes Applied!');
    console.log('📋 Required Fields: Name, Main Category, Category, Price, Colors, Sizes, Stock, Image');
  }, []);

  const sizePresetOptions = useMemo(
    () => getSizePresetsForMainCategoryName(newProduct.mainCategory),
    [newProduct.mainCategory]
  );

  useEffect(() => {
    if (!String(newProduct.mainCategory || '').trim()) return;
    const preset = getSizePresetsForMainCategoryName(newProduct.mainCategory);
    const allowed = new Set(preset);
    setNewProduct((prev) => {
      const prevSizes = prev.sizes || [];
      const nextSizes = prevSizes.filter((s) => allowed.has(s));
      if (nextSizes.length === prevSizes.length) return prev;
      const removed = prevSizes.filter((s) => !allowed.has(s));
      const nextStock = pruneStockRemovedSizes(prev.stock || {}, removed);
      return { ...prev, sizes: nextSizes, stock: nextStock };
    });
  }, [newProduct.mainCategory]);

  // Populate form when editing a product - Fetch complete product data from API
  useEffect(() => {
    const loadProductForEdit = async () => {
      if (editingProduct) {
        const productId = getMongoProductId(editingProduct);

        if (!productId) {
          console.error('❌ No MongoDB product ID found in editingProduct:', editingProduct);
          showCustomAlert('Product ID not found. Please try again.', 'error');
          return;
        }

        try {
          console.log('📥 Fetching complete product data for editing:', productId);

          const [response, categoriesResult] = await Promise.all([
            productService.getProductForEdit(productId),
            categoryService.getCategories().catch(() => ({ categories: [] })),
          ]);

          if (response.success && response.product) {
            const product = response.product;
            console.log('✅ Product data loaded for editing:', product);

            let categoriesData = [];
            if (categoriesResult?.categories) {
              categoriesData = Array.isArray(categoriesResult.categories)
                ? categoriesResult.categories
                : Array.isArray(categoriesResult.categories?.data)
                  ? categoriesResult.categories.data
                  : [];
            }

            const categoryPick = resolveProductCategorySelection(product, categoriesData);
            
            // Format images properly
            const rawProductImages = product.images || [];
            let primaryOrigIdx = rawProductImages.findIndex(
              (im) => im && typeof im === 'object' && (im.isPrimary === true || im.primary === true)
            );
            if (primaryOrigIdx < 0 && typeof product.primaryImageIndex === 'number') {
              primaryOrigIdx = product.primaryImageIndex;
            }
            if (primaryOrigIdx < 0) primaryOrigIdx = 0;

            // ✅ Format images and ensure all required fields are present
            const formattedImages = rawProductImages
              .map((image, origIdx) => ({ image, origIdx }))
              .filter(({ image }) => {
                if (typeof image === 'string') return Boolean(image.trim());
                return image && (image.url || image.cloudinaryUrl);
              })
              .map(({ image, origIdx }) => {
                if (typeof image === 'string') {
                  return {
                    file: null,
                    url: image,
                    cloudinaryUrl: image,
                    public_id: '',
                    asset_id: '',
                    width: 0,
                    height: 0,
                    format: 'jpg',
                    bytes: 0,
                    originalName: 'image',
                    uploadedAt: new Date().toISOString(),
                    isPrimary: origIdx === primaryOrigIdx,
                  };
                }
                const linked = getLinkedColorFromImageAdmin(image);
                return {
                  file: null,
                  url: image.url || image.cloudinaryUrl,
                  cloudinaryUrl: image.url || image.cloudinaryUrl,
                  public_id: image.public_id,
                  asset_id: image.asset_id || image.assetId || '',
                  width: image.width,
                  height: image.height,
                  format: image.format,
                  bytes: image.bytes,
                  originalName: image.originalName,
                  uploadedAt: image.uploadedAt || image.uploaded_at || new Date().toISOString(),
                  isPrimary: origIdx === primaryOrigIdx,
                  ...(linked ? { linkedColor: linked } : {}),
                };
              });
            
            console.log('✅ Formatted images count:', formattedImages.length);
            console.log('✅ Formatted images:', formattedImages);
            
            // Populate form with complete product data
            // ✅ Ensure colors and sizes are arrays (not objects)
            let colorsArray = Array.isArray(product.colors) 
              ? product.colors 
              : (product.colors ? Object.values(product.colors).map(c => typeof c === 'string' ? c : (c.name || c.color || String(c))) : []);
            
            let sizesArray = Array.isArray(product.sizes) 
              ? product.sizes 
              : (product.sizes ? Object.values(product.sizes).map(s => typeof s === 'string' ? s : (s.name || s.size || String(s))) : []);
            
            colorsArray = colorsArray.filter((c) => !isPlaceholderColorKey(c));
            sizesArray = sizesArray.filter((s) => !isPlaceholderSizeKey(s));
            colorsArray = mergeVariantColorsFromImageLinks(colorsArray, formattedImages);

            console.log('✅ Colors array:', colorsArray);
            console.log('✅ Sizes array:', sizesArray);

            let stockForForm = product.stock || {};
            // Keep variant-wise stock when either color or size axis exists.
            // Collapse to simple default/default ONLY when both axes are absent.
            if (colorsArray.length === 0 && sizesArray.length === 0) {
              const agg = aggregateNestedStock(stockForForm);
              stockForForm =
                agg > 0
                  ? { [SIMPLE_STOCK_COLOR]: { [SIMPLE_STOCK_SIZE]: agg } }
                  : {};
            }

            const rawDescProd = product.description || '';
            const strippedDesc = stripEmbedFromDescriptionForDisplay(rawDescProd);
            const legacyBlocks = mergeDescriptionRichSources(product.descriptionRich, rawDescProd);
            let descriptionField = strippedDesc || '';
            let descFmt = DESCRIPTION_FORMAT.PLAIN;
            if (legacyBlocks && hasRenderableRichContent(legacyBlocks)) {
              descriptionField = blocksToLegacyHtml(legacyBlocks);
              descFmt = DESCRIPTION_FORMAT.HTML;
            } else if (strippedDesc && isLikelyHtmlDescription(strippedDesc)) {
              descriptionField = strippedDesc;
              descFmt = DESCRIPTION_FORMAT.HTML;
            }
            setDescriptionFormat(descFmt);
            setDescLoadNonce((n) => n + 1);
            setCustomColorPalette(extractHexPaletteFromColorNames(colorsArray));

            const { originalPrice: formOriginal, salePrice: formSale } =
              pricesFromStoredProduct(product);

            setNewProduct({
              ...EMPTY_NEW_PRODUCT,
              name: product.name || '',
              description: descriptionField,
              shortDescription: product.shortDescription || '',
              originalPrice: formOriginal,
              salePrice: formSale,
              mainCategory: categoryPick.mainCategoryName || product.mainCategory || '',
              category: categoryPick.mainCategoryId || '',
              subcategory:
                categoryPick.subcategoryId ||
                categoryPick.subcategoryName ||
                product.subcategory ||
                '',
              childrenCategory: categoryPick.childrenCategoryId || product.childrenCategory || '',
              brand: product.brand || '',
              sku: product.sku || '',
              url: product.url || '',
              productId: product.productId || product.id || product._id || '',
              colors: colorsArray, // ✅ Array of strings
              sizes: sizesArray,    // ✅ Array of strings
              images: formattedImages.map(img => img.url || img.cloudinaryUrl),
              stock: stockForForm,
              tags: Array.isArray(product.tags) ? product.tags : [],
              features: Array.isArray(product.features) ? product.features : [],
              specifications: product.specifications || {},
              ...readProductStatusFlags(product),
              // Map status: if status field exists, use it; otherwise derive from isActive
              status: product.status || (product.isActive !== undefined ? (product.isActive ? 'active' : 'inactive') : 'active'),
              isActive: product.isActive !== undefined ? product.isActive : (product.status === 'active'),
              weight: product.weight || '',
              dimensions: product.dimensions || { length: '', width: '', height: '' },
              saleEndDate: product.saleEndDate ? new Date(product.saleEndDate).toISOString().split('T')[0] : ''
            });

            // Set uploaded images
            setUploadedImages(formattedImages);
            applyLinkedColorDraftStateFromImages(
              formattedImages,
              setHexDraftByImage,
              setImageColorInput
            );

            const vUrl =
              (typeof product.videoUrl === 'string' && product.videoUrl.trim()) ||
              (product.video && typeof product.video === 'object' && (product.video.url || product.video.secure_url)) ||
              '';
            setPendingVideo((prev) => {
              if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
              return null;
            });
            setExistingVideoUrl(vUrl ? String(vUrl).trim() : '');

            console.log('✅ Form populated with complete product data');
            console.log('✅ Uploaded images set:', formattedImages);
            console.log('✅ Stock data loaded:', product.stock);
            console.log('✅ Stock structure:', JSON.stringify(product.stock, null, 2));
          } else {
            throw new Error('Failed to load product data');
          }
        } catch (error) {
          console.error('❌ Error loading product for editing:', error);
          
          // Extract error message
          let errorMessage = error.message || 'Failed to load product data';
          try {
            const errorObj = JSON.parse(errorMessage);
            if (errorObj.error) errorMessage = errorObj.error;
            else if (errorObj.message) errorMessage = errorObj.message;
          } catch (e) {
            // Not a JSON string, use as is
          }
          
          showCustomAlert('Failed to load product data: ' + errorMessage, 'error');
          
          // Fallback: Use editingProduct data directly if API fails
          console.log('⚠️ Falling back to editingProduct data:', editingProduct);
          const rawFb = editingProduct.description || '';
          const strippedFb = stripEmbedFromDescriptionForDisplay(rawFb);
          const legacyFb = mergeDescriptionRichSources(editingProduct.descriptionRich, rawFb);
          let descFb = strippedFb || '';
          let fmtFb = DESCRIPTION_FORMAT.PLAIN;
          if (legacyFb && hasRenderableRichContent(legacyFb)) {
            descFb = blocksToLegacyHtml(legacyFb);
            fmtFb = DESCRIPTION_FORMAT.HTML;
          } else if (strippedFb && isLikelyHtmlDescription(strippedFb)) {
            descFb = strippedFb;
            fmtFb = DESCRIPTION_FORMAT.HTML;
          }
          setDescriptionFormat(fmtFb);
          setDescLoadNonce((n) => n + 1);

          let fbColors = Array.isArray(editingProduct.colors) ? editingProduct.colors : [];
          let fbSizes = Array.isArray(editingProduct.sizes) ? editingProduct.sizes : [];
          fbColors = fbColors.filter((c) => !isPlaceholderColorKey(c));
          fbSizes = fbSizes.filter((s) => !isPlaceholderSizeKey(s));

          let formattedImagesFb = [];
          if (editingProduct.images && editingProduct.images.length > 0) {
            const rawFb = editingProduct.images;
            let pIdxFb = rawFb.findIndex(
              (im) => im && typeof im === 'object' && (im.isPrimary === true || im.primary === true)
            );
            if (pIdxFb < 0 && typeof editingProduct.primaryImageIndex === 'number') {
              pIdxFb = editingProduct.primaryImageIndex;
            }
            if (pIdxFb < 0) pIdxFb = 0;

            formattedImagesFb = rawFb
              .map((image, origIdx) => ({ image, origIdx }))
              .filter(({ image }) => {
                if (typeof image === 'string') return Boolean(image.trim());
                return image && (image.url || image.cloudinaryUrl);
              })
              .map(({ image, origIdx }) => {
                if (typeof image === 'string') {
                  return {
                    file: null,
                    url: image,
                    cloudinaryUrl: image,
                    public_id: '',
                    asset_id: '',
                    width: 0,
                    height: 0,
                    format: 'jpg',
                    bytes: 0,
                    originalName: 'image',
                    uploadedAt: new Date().toISOString(),
                    isPrimary: origIdx === pIdxFb,
                  };
                }
                const linked = getLinkedColorFromImageAdmin(image);
                return {
                  file: null,
                  url: image.url || image.cloudinaryUrl,
                  cloudinaryUrl: image.url || image.cloudinaryUrl,
                  public_id: image.public_id,
                  asset_id: image.asset_id || image.assetId || '',
                  width: image.width,
                  height: image.height,
                  format: image.format,
                  bytes: image.bytes,
                  originalName: image.originalName,
                  uploadedAt: image.uploadedAt || image.uploaded_at || new Date().toISOString(),
                  isPrimary: origIdx === pIdxFb,
                  ...(linked ? { linkedColor: linked } : {}),
                };
              });
          }

          fbColors = mergeVariantColorsFromImageLinks(fbColors, formattedImagesFb);
          setCustomColorPalette(extractHexPaletteFromColorNames(fbColors));

          let fbStock = editingProduct.stock || {};
          // Same rule as main path: preserve variant buckets unless both axes are missing.
          if (fbColors.length === 0 && fbSizes.length === 0) {
            const agg = aggregateNestedStock(fbStock);
            fbStock =
              agg > 0 ? { [SIMPLE_STOCK_COLOR]: { [SIMPLE_STOCK_SIZE]: agg } } : {};
          }

          const fbCategory =
            typeof editingProduct.category === 'object'
              ? editingProduct.category._id || editingProduct.category.id
              : editingProduct.category;

          const { originalPrice: fbOriginal, salePrice: fbSale } =
            pricesFromStoredProduct(editingProduct);

          setNewProduct({
            ...EMPTY_NEW_PRODUCT,
            name: editingProduct.name || '',
            description: descFb,
            originalPrice: fbOriginal,
            salePrice: fbSale,
            mainCategory: editingProduct.mainCategory || '',
            category: fbCategory || '',
            subcategory: editingProduct.subcategory || '',
            childrenCategory: editingProduct.childrenCategory || '',
            brand: editingProduct.brand || '',
            sku: editingProduct.sku || '',
            url: editingProduct.url || '',
            productId: editingProduct.productId || '',
            colors: fbColors,
            sizes: fbSizes,
            images: editingProduct.images || [],
            stock: fbStock,
            ...readProductStatusFlags(editingProduct),
            status: editingProduct.status || 'active'
          });

          if (formattedImagesFb.length > 0) {
            setUploadedImages(formattedImagesFb);
            applyLinkedColorDraftStateFromImages(
              formattedImagesFb,
              setHexDraftByImage,
              setImageColorInput
            );
            console.log('⚠️ Fallback images formatted:', formattedImagesFb);
          } else {
            setUploadedImages([]);
          }
        }
      } else {
        // Reset form for new product (colors/sizes optional — set stock below)
        setNewProduct({ ...EMPTY_NEW_PRODUCT });
        setDescriptionFormat(DESCRIPTION_FORMAT.HTML);
        setDescLoadNonce((n) => n + 1);
        setCustomColorPalette([]);
        setUploadedImages([]);
      }
    };

    loadProductForEdit();
  }, [editingProduct]);

  const showCustomAlert = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    
    // Auto hide after 5 seconds
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  const applyHexToImage = useCallback(
    (index) => {
      const raw = hexDraftByImage[index] || '#888888';
      const h = normalizeHexColor(raw);
      if (!h) {
        showCustomAlert('Pick a valid hex color (#RRGGBB).', 'error');
        return;
      }
      setImageLinkedColor(index, h);
      setImageColorInput((prev) => ({ ...prev, [index]: h }));
    },
    [hexDraftByImage, showCustomAlert, setImageLinkedColor]
  );

  const pickImageColorSuggestion = useCallback(
    (index, colorName) => {
      setImageLinkedColor(index, colorName);
      setImageColorInput((prev) => ({ ...prev, [index]: colorName }));
      setOpenImageColorSuggest(null);
    },
    [setImageLinkedColor]
  );

  const commitImageColorFromInput = useCallback(
    (index) => {
      const raw = imageColorInput[index];
      const q = raw !== undefined ? String(raw).trim() : '';
      if (!q) {
        setImageLinkedColor(index, '');
        return;
      }
      const hex = normalizeHexColor(q);
      if (hex) {
        setImageLinkedColor(index, hex);
        setImageColorInput((prev) => ({ ...prev, [index]: hex }));
        return;
      }
      const exact = searchableColorsForImageLink.find(
        (c) => c.name.toLowerCase() === q.toLowerCase()
      );
      if (exact) {
        setImageLinkedColor(index, exact.name);
        setImageColorInput((prev) => ({ ...prev, [index]: exact.name }));
        return;
      }
      const matches = filterColorsByQuery(searchableColorsForImageLink, q);
      if (matches.length === 1) {
        setImageLinkedColor(index, matches[0].name);
        setImageColorInput((prev) => ({ ...prev, [index]: matches[0].name }));
        return;
      }
      setImageColorInput((prev) => ({
        ...prev,
        [index]: uploadedImages[index]?.linkedColor ?? '',
      }));
    },
    [imageColorInput, searchableColorsForImageLink, setImageLinkedColor, uploadedImages]
  );

  const handleImageColorFieldFocus = useCallback(
    (index) => {
      if (imageColorBlurTimerRef.current) {
        clearTimeout(imageColorBlurTimerRef.current);
        imageColorBlurTimerRef.current = null;
      }
      setOpenImageColorSuggest(index);
      setImageColorInput((prev) => ({
        ...prev,
        [index]:
          prev[index] !== undefined
            ? prev[index]
            : uploadedImages[index]?.linkedColor ?? '',
      }));
    },
    [uploadedImages]
  );

  const handleImageColorFieldBlur = useCallback(
    (index) => {
      imageColorBlurTimerRef.current = setTimeout(() => {
        setOpenImageColorSuggest(null);
        commitImageColorFromInput(index);
        imageColorBlurTimerRef.current = null;
      }, 200);
    },
    [commitImageColorFromInput]
  );

  const handleImageColorFieldChange = useCallback((index, value) => {
    setImageColorInput((prev) => ({ ...prev, [index]: value }));
    setOpenImageColorSuggest(index);
  }, []);

  const commitGlobalQuickColorFromInput = useCallback(() => {
    const q = String(globalQuickColorInput ?? '').trim();
    if (!q) return;
    const hex = normalizeHexColor(q);
    if (hex) {
      addQuickVariantColor(hex);
      setGlobalQuickColorInput('');
      setOpenGlobalQuickColorSuggest(false);
      return;
    }
    const exact = searchableColorsForImageLink.find(
      (c) => c.name.toLowerCase() === q.toLowerCase()
    );
    if (exact) {
      addQuickVariantColor(exact.name);
      setGlobalQuickColorInput('');
      setOpenGlobalQuickColorSuggest(false);
      return;
    }
    const matches = filterColorsByQuery(searchableColorsForImageLink, q);
    if (matches.length === 1) {
      addQuickVariantColor(matches[0].name);
      setGlobalQuickColorInput('');
      setOpenGlobalQuickColorSuggest(false);
      return;
    }
    showCustomAlert(
      matches.length === 0
        ? 'No matching color. Pick a suggestion or use a hex like #FF0000.'
        : 'Several colors match — choose one from the list.',
      'error'
    );
  }, [
    globalQuickColorInput,
    searchableColorsForImageLink,
    addQuickVariantColor,
    showCustomAlert,
  ]);

  const pickGlobalQuickColorSuggestion = useCallback(
    (colorName) => {
      addQuickVariantColor(colorName);
      setGlobalQuickColorInput('');
      setOpenGlobalQuickColorSuggest(false);
    },
    [addQuickVariantColor]
  );

  const applyGlobalHexToProduct = useCallback(() => {
    const h = normalizeHexColor(globalHexDraft || '#888888');
    if (!h) {
      showCustomAlert('Pick a valid hex color (#RRGGBB).', 'error');
      return;
    }
    addQuickVariantColor(h);
    setGlobalHexDraft(h);
  }, [globalHexDraft, addQuickVariantColor, showCustomAlert]);

  const handleGlobalEyeDropper = useCallback(async () => {
    if (typeof window === 'undefined' || !window.EyeDropper) {
      showCustomAlert(
        'Screen eyedropper works in Chrome and Edge. Use the color wheel below.',
        'error'
      );
      return;
    }
    try {
      const eyeDropper = new window.EyeDropper();
      const { sRGBHex } = await eyeDropper.open();
      const h = normalizeHexColor(sRGBHex);
      if (h) {
        setGlobalHexDraft(h);
        addQuickVariantColor(h);
      }
    } catch {
      /* cancelled */
    }
  }, [showCustomAlert, addQuickVariantColor]);

  const handleEyeDropperForImage = useCallback(
    async (index) => {
      if (typeof window === 'undefined' || !window.EyeDropper) {
        showCustomAlert(
          'Screen eyedropper works in Chrome and Edge. Use the color wheel on this image card.',
          'error'
        );
        return;
      }
      try {
        const eyeDropper = new window.EyeDropper();
        const { sRGBHex } = await eyeDropper.open();
        const h = normalizeHexColor(sRGBHex);
        if (h) {
          setHexDraftByImage((prev) => ({ ...prev, [index]: h }));
          setImageLinkedColor(index, h);
          setImageColorInput((prev) => ({ ...prev, [index]: h }));
        }
      } catch {
        /* cancelled */
      }
    },
    [showCustomAlert, setImageLinkedColor]
  );

  const cancelPhotoColorPick = useCallback(() => setPhotoColorPick(null), []);

  const applySampledHex = useCallback(
    (hex, scope, imageIndex) => {
      const h = normalizeHexColor(hex);
      if (!h) return;
      if (scope === 'image' && imageIndex != null) {
        setImageLinkedColor(imageIndex, h);
        setImageColorInput((prev) => ({ ...prev, [imageIndex]: h }));
        setHexDraftByImage((prev) => ({ ...prev, [imageIndex]: h }));
      } else {
        setGlobalHexDraft(h);
        addQuickVariantColor(h);
      }
      setPhotoColorPick(null);
    },
    [setImageLinkedColor, addQuickVariantColor]
  );

  const handleProductPhotoColorSample = useCallback(
    (imageIndex, event) => {
      if (!photoColorPick) return;
      if (photoColorPick.scope === 'image' && photoColorPick.index !== imageIndex) return;
      const coords = getPointerClientCoords(event);
      if (!coords) return;
      event.preventDefault();
      event.stopPropagation();
      const img = event.currentTarget;
      const hex = sampleHexFromImageElement(img, coords.clientX, coords.clientY);
      if (!hex) {
        showCustomAlert(
          'Could not read that pixel. Use a newly uploaded photo or the color wheel.',
          'error'
        );
        return;
      }
      applySampledHex(hex, photoColorPick.scope, imageIndex);
    },
    [photoColorPick, showCustomAlert, applySampledHex]
  );

  const startGlobalColorPick = useCallback(() => {
    if (uploadedImages.length === 0) {
      showCustomAlert('Upload at least one product photo first, or use the color wheel.', 'error');
      return;
    }
    setPhotoColorPick({ scope: 'global' });
  }, [uploadedImages.length, showCustomAlert]);

  const startImageColorPick = useCallback((index) => {
    setPhotoColorPick({ scope: 'image', index });
  }, []);

  const handleGlobalColorPickButton = useCallback(async () => {
    if (!prefersPhotoColorPicker()) {
      await handleGlobalEyeDropper();
      return;
    }
    startGlobalColorPick();
  }, [handleGlobalEyeDropper, startGlobalColorPick]);

  const handleImageColorPickButton = useCallback(
    async (index) => {
      if (!prefersPhotoColorPicker()) {
        await handleEyeDropperForImage(index);
        return;
      }
      startImageColorPick(index);
    },
    [handleEyeDropperForImage, startImageColorPick]
  );

  /** Remove a variant color from the product, stock rows, and any photo links. */
  const removeVariantColor = useCallback(
    (colorToRemove) => {
      const key = String(colorToRemove ?? '').trim();
      if (!key) return;

      const targetHex = normalizeHexColor(key);
      const matchesKey = (c) => {
        const cs = String(c ?? '').trim();
        if (!cs) return false;
        if (targetHex && normalizeHexColor(cs)) {
          return normalizeHexColor(cs) === targetHex;
        }
        return cs === key;
      };

      setNewProduct((prev) => {
        if (!prev.colors.some((c) => matchesKey(c))) return prev;

        if (prev.colors.length <= 1) {
          queueMicrotask(() =>
            showCustomAlert(
              'At least one variant color is required. Add another color before removing this one.',
              'error'
            )
          );
          return prev;
        }

        const nextColors = prev.colors.filter((c) => !matchesKey(c));
        const nextStock = { ...prev.stock };
        Object.keys(nextStock).forEach((k) => {
          if (matchesKey(k)) delete nextStock[k];
        });

        queueMicrotask(() => {
          setUploadedImages((imgs) =>
            imgs.map((img) => {
              const lc = img.linkedColor;
              if (!lc) return img;
              const ha = normalizeHexColor(String(lc));
              const hb = normalizeHexColor(key);
              if (ha && hb && ha === hb) return { ...img, linkedColor: undefined };
              if (String(lc).trim() === key) return { ...img, linkedColor: undefined };
              return img;
            })
          );
          if (targetHex) {
            setCustomColorPalette((p) => p.filter((c) => normalizeHexColor(c.name) !== targetHex));
          }
        });

        return { ...prev, colors: nextColors, stock: nextStock };
      });
    },
    [showCustomAlert]
  );

  // ✅ Authentication functions removed - no longer required

  const handleInputChange = (field, value) => {
    setNewProduct((prev) => ({
      ...prev,
      [field]: value == null ? '' : value,
    }));
  };

  const handleArrayChange = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDescriptionFormatChange = (next, options = {}) => {
    if (next === DESCRIPTION_FORMAT.PLAIN && descriptionFormat === DESCRIPTION_FORMAT.HTML) {
      let plain;
      if (options.plainText != null) {
        plain = options.plainText;
      } else {
        const source =
          options.htmlSnapshot != null
            ? options.htmlSnapshot
            : sanitizeProductDescriptionHtml(newProduct.description || '');
        plain = stripHtmlToPlain(source);
      }
      setNewProduct((prev) => ({ ...prev, description: plain }));
    }
    if (next === DESCRIPTION_FORMAT.HTML && descriptionFormat === DESCRIPTION_FORMAT.PLAIN) {
      setNewProduct((prev) => {
        const t = (prev.description || '').trim();
        return { ...prev, description: t ? plainLinesToHtml(t) : '<p><br></p>' };
      });
    }
    setDescriptionFormat(next);
  };

  // Category handlers
  const handleCategoryChange = (categoryId) => {
    setNewProduct((prev) => ({
      ...prev,
      category: categoryId ?? '',
      subcategory: '',
      childrenCategory: '',
    }));
    
    // Auto-generate SKU when category changes (only for new products)
    if (autoGenerateSKU && !editingProduct && newProduct.name) {
      generateNewSKU(newProduct.name);
    }
  };

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (newProduct.category) {
        const result = await categoryService.getSubcategories(newProduct.category);
        if (result.success && Array.isArray(result.subcategories) && result.subcategories.length > 0) {
          const subcategoryNames = result.subcategories.map((sub) =>
            typeof sub === 'string' ? sub : sub.name || sub.subcategory || sub
          );
          setAvailableSubcategories(subcategoryNames);
        } else {
          setAvailableSubcategories([]);
        }
      } else {
        setAvailableSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [newProduct.category]);

  // Function to generate new SKU
  const generateNewSKU = async (productName) => {
    try {
      // Get existing products to calculate next sequence number
      const existingProducts = products || [];
      
      const newSKU = generateAutoSKU(productName, existingProducts);
      const productId = generateProductIdFromSKU(newSKU);
      const creatorSlug = resolveCreatorSlugForAdminUser(currentUser);
      const seoUrl = buildProductSeoSlug({
        name: productName,
        creatorSlug,
        productId,
        sku: newSKU,
        currentUser,
      });
      
      setNewProduct(prev => ({
        ...prev,
        sku: newSKU,
        url: seoUrl,
        productId: productId
      }));
      
      // Update preview
      const preview = generateSKUPreview(productName);
      setSkuPreview(preview);
      
      console.log('🔢 Auto-generated SKU:', newSKU);
      console.log('🔗 SEO URL:', seoUrl);
      console.log('🆔 Product ID:', productId);
    } catch (error) {
      console.error('Error generating SKU:', error);
    }
  };

  const handleSubcategoryChange = (subcategoryId) => {
    setNewProduct((prev) => ({
      ...prev,
      subcategory: subcategoryId ?? '',
      childrenCategory: '',
    }));
  };

  // Initialize tags input - don't auto-populate from tags array (keep input empty for new tags)
  useEffect(() => {
    // Only clear input if tags array is empty and we're not editing
    if ((!newProduct.tags || newProduct.tags.length === 0) && !editingProduct) {
      if (tagsInput !== '') {
        setTagsInput('');
      }
    }
  }, []); // Only run once on mount

  // Handle tags input change (single tag input)
  const handleTagsInputChange = (value) => {
    setTagsInput(value);
  };

  // Add tag to the tags array
  const handleAddTag = () => {
    const tagValue = tagsInput.trim();
    if (tagValue && tagValue.length > 0) {
      // Check if tag already exists
      const existingTags = newProduct.tags || [];
      if (!existingTags.includes(tagValue)) {
        setNewProduct(prev => ({
          ...prev,
          tags: [...existingTags, tagValue]
        }));
        // Clear input after adding
        setTagsInput('');
      } else {
        // Tag already exists, just clear input
        setTagsInput('');
      }
    }
  };

  // Remove tag from tags array
  const handleRemoveTag = (tagToRemove) => {
    setNewProduct(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle Enter key press to add tag
  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Handle subcategory suggestion click
  const handleSubcategorySuggestionClick = (subcategory) => {
    const existingTags = newProduct.tags || [];
    if (!existingTags.includes(subcategory)) {
      setNewProduct(prev => ({
        ...prev,
        tags: [...existingTags, subcategory]
      }));
    }
    setShowSubcategorySuggestions(false);
  };

  const handleQuickMaterialClick = (value) => {
    const v = String(value).trim();
    if (!v) return;
    setNewProduct((prev) => {
      const existing = prev.tags || [];
      if (existing.includes(v)) {
        return { ...prev, tags: existing.filter((tag) => tag !== v) };
      }
      return { ...prev, tags: [...existing, v] };
    });
  };

  // ✅ CORRECT - Store images locally, don't upload immediately
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    console.log('📁 Storing images locally (no immediate upload):', files.length);
    
    // Process each file and store locally
    const newImages = [];
    
    files.forEach((file, index) => {
      // Create object URL for preview
      const previewUrl = URL.createObjectURL(file);
      
      newImages.push({
        file: file,
        previewUrl: previewUrl,
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      console.log(`📁 Image ${index + 1} stored locally:`, {
        name: file.name,
        size: file.size,
        type: file.type
      });
    });
    
    if (newImages.length > 0) {
      setUploadedImages((prev) => {
        const batch = newImages.map((img) => ({ ...img, isPrimary: false }));
        if (prev.length === 0 && batch.length > 0) {
          batch[0].isPrimary = true;
        }
        return [...prev, ...batch];
      });
      console.log('✅ All images stored locally for form submission');
      console.log('📸 Total images now:', newImages.length + uploadedImages.length);
    }
  };

  const setPrimaryUploadedImage = (index) => {
    setUploadedImages((prev) =>
      prev.map((img, i) => ({ ...img, isPrimary: i === index }))
    );
  };

  const removeImage = (index) => {
    const imageToRemove = uploadedImages[index];
    if (imageToRemove && imageToRemove.previewUrl) {
      URL.revokeObjectURL(imageToRemove.previewUrl);
    }

    setUploadedImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) return next;
      if (!next.some((i) => i.isPrimary)) {
        return next.map((img, i) => ({ ...img, isPrimary: i === 0 }));
      }
      return next;
    });
    console.log(`🗑️ Removed image at index ${index}`);
  };

  const handleProductVideoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.size > MAX_PRODUCT_VIDEO_BYTES) {
      showCustomAlert(`Video must be ${MAX_PRODUCT_VIDEO_BYTES / (1024 * 1024)}MB or smaller.`, 'error');
      event.target.value = '';
      return;
    }
    if (pendingVideo?.previewUrl) {
      URL.revokeObjectURL(pendingVideo.previewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingVideo({
      file,
      previewUrl,
      name: file.name,
      size: file.size,
    });
    setExistingVideoUrl('');
  };

  const removeProductVideo = () => {
    if (pendingVideo?.previewUrl) {
      URL.revokeObjectURL(pendingVideo.previewUrl);
    }
    setPendingVideo(null);
    setExistingVideoUrl('');
    if (productVideoInputRef.current) {
      productVideoInputRef.current.value = '';
    }
  };

  // Stock Management Functions — nested shape: stock[color][size]; use default/default for missing dimension
  const initializeStock = useCallback(() => {
    setNewProduct((prev) => {
      const simpleSeed = parseInt(prev.stock?.[SIMPLE_STOCK_COLOR]?.[SIMPLE_STOCK_SIZE], 10) || 0;
      const stock = {};
      const hasColors = prev.colors.length > 0;
      const hasSizes = prev.sizes.length > 0;

      if (hasColors && hasSizes) {
        prev.colors.forEach((color, ci) => {
          stock[color] = {};
          prev.sizes.forEach((size, si) => {
            const existing = prev.stock[color]?.[size];
            if (existing !== undefined && existing !== '') {
              stock[color][size] = existing;
            } else if (simpleSeed > 0 && ci === 0 && si === 0) {
              stock[color][size] = simpleSeed;
            } else {
              stock[color][size] = 0;
            }
          });
        });
      } else if (hasColors && !hasSizes) {
        prev.colors.forEach((color, ci) => {
          stock[color] = {};
          const existing = prev.stock[color]?.[SIMPLE_STOCK_SIZE];
          if (existing !== undefined && existing !== '') {
            stock[color][SIMPLE_STOCK_SIZE] = existing;
          } else if (simpleSeed > 0 && ci === 0) {
            stock[color][SIMPLE_STOCK_SIZE] = simpleSeed;
          } else {
            stock[color][SIMPLE_STOCK_SIZE] = 0;
          }
        });
      } else if (!hasColors && hasSizes) {
        stock[SIMPLE_STOCK_COLOR] = {};
        prev.sizes.forEach((size, si) => {
          const existing = prev.stock[SIMPLE_STOCK_COLOR]?.[size];
          if (existing !== undefined && existing !== '') {
            stock[SIMPLE_STOCK_COLOR][size] = existing;
          } else if (simpleSeed > 0 && si === 0) {
            stock[SIMPLE_STOCK_COLOR][size] = simpleSeed;
          } else {
            stock[SIMPLE_STOCK_COLOR][size] = 0;
          }
        });
      } else {
        const v = prev.stock?.[SIMPLE_STOCK_COLOR]?.[SIMPLE_STOCK_SIZE];
        stock[SIMPLE_STOCK_COLOR] = {
          [SIMPLE_STOCK_SIZE]: v !== undefined && v !== '' ? v : 0,
        };
      }
      return { ...prev, stock };
    });
  }, []);

  const updateStock = (color, size, quantity) => {
    // Handle empty string - allow empty for better UX during typing
    const quantityStr = String(quantity || '');
    if (quantityStr.trim() === '' || quantity === null || quantity === undefined) {
      setNewProduct(prev => ({
        ...prev,
        stock: {
          ...prev.stock,
          [color]: {
            ...prev.stock[color],
            [size]: ''
          }
        }
      }));
      return;
    }
    
    // Parse the value, default to 0 if invalid
    const parsedValue = parseInt(quantityStr);
    const value = isNaN(parsedValue) ? 0 : parsedValue;
    
    setNewProduct(prev => ({
      ...prev,
      stock: {
        ...prev.stock,
        [color]: {
          ...prev.stock[color],
          [size]: value
        }
      }
    }));
  };

  const getTotalStock = () => {
    let total = 0;
    Object.values(newProduct.stock || {}).forEach((colorStock) => {
      Object.values(colorStock || {}).forEach((sizeStock) => {
        const n = parseInt(sizeStock, 10);
        total += Number.isNaN(n) ? 0 : n;
      });
    });
    return total;
  };

  /** Plain nested numbers for product create/update — backend validates active products against this before merge. */
  const buildStockPayloadForProductApi = (stock) => {
    if (!stock || typeof stock !== 'object') return {};
    const out = {};
    Object.keys(stock).forEach((color) => {
      const row = stock[color];
      if (!row || typeof row !== 'object') return;
      const ck = String(color).trim();
      if (!ck) return;
      out[ck] = {};
      Object.keys(row).forEach((size) => {
        const sk = String(size).trim();
        if (!sk) return;
        const n = parseInt(row[size], 10);
        out[ck][sk] = Number.isNaN(n) || n < 0 ? 0 : n;
      });
    });
    return out;
  };

  const getColorStock = (color) => {
    let total = 0;
    if (newProduct.stock[color]) {
      Object.values(newProduct.stock[color]).forEach(sizeStock => {
        const n = parseInt(sizeStock, 10);
        total += Number.isNaN(n) ? 0 : n;
      });
    }
    return total;
  };

  // Initialize stock structure when colors/sizes change (simple / color-only / size-only / matrix)
  useEffect(() => {
    const cLen = newProduct.colors.length;
    const sLen = newProduct.sizes.length;

    if (cLen === 0 && sLen === 0) {
      const keys = Object.keys(newProduct.stock || {});
      const onlySimpleCell =
        keys.length === 1 &&
        keys[0] === SIMPLE_STOCK_COLOR &&
        newProduct.stock[SIMPLE_STOCK_COLOR] &&
        Object.keys(newProduct.stock[SIMPLE_STOCK_COLOR]).length === 1 &&
        Object.prototype.hasOwnProperty.call(
          newProduct.stock[SIMPLE_STOCK_COLOR],
          SIMPLE_STOCK_SIZE
        );
      if (!onlySimpleCell) {
        initializeStock();
      }
      return;
    }

    if (cLen > 0 && sLen > 0) {
      const hasStock = newProduct.stock && Object.keys(newProduct.stock).length > 0;
      const needsInitialization =
        !hasStock ||
        newProduct.colors.some(
          (color) =>
            !newProduct.stock[color] ||
            newProduct.sizes.some((size) => newProduct.stock[color][size] === undefined)
        );
      if (needsInitialization) initializeStock();
      return;
    }

    if (cLen > 0) {
      const needsInitialization = newProduct.colors.some(
        (color) =>
          !newProduct.stock[color] ||
          newProduct.stock[color][SIMPLE_STOCK_SIZE] === undefined
      );
      if (needsInitialization) initializeStock();
      return;
    }

    if (sLen > 0) {
      const needsInitialization =
        !newProduct.stock[SIMPLE_STOCK_COLOR] ||
        newProduct.sizes.some(
          (size) => newProduct.stock[SIMPLE_STOCK_COLOR][size] === undefined
        );
      if (needsInitialization) initializeStock();
    }
    // Intentionally omit newProduct.stock: we only reshape when variant axes change, not on each qty keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProduct.colors, newProduct.sizes, initializeStock]);


  const handleAddProduct = async (statusOverride = null) => {
    try {
      
      // Set status based on button clicked
      // If Add Product button clicked (no override), set to 'active'
      // If Draft button clicked (override = 'draft'), set to 'draft' (which maps to inactive)
      let finalStatus;
      if (statusOverride === 'draft') {
        finalStatus = 'draft';
      } else {
        finalStatus = 'active'; // Default to active for Add Product button
      }
      
      console.log('🔍 Status Override:', statusOverride);
      console.log('🔍 Final Status:', finalStatus);
      
      // NO VALIDATION - All fields are optional
      console.log('🔍 Product data (no validation):', {
        name: newProduct.name,
        description: newProduct.description,
        originalPrice: newProduct.originalPrice,
        category: newProduct.category,
        brand: newProduct.brand,
        subcategory: newProduct.subcategory,
        uploadedImages: uploadedImages,
        colors: newProduct.colors,
        sizes: newProduct.sizes,
        stock: newProduct.stock,
        status: finalStatus
      });
      
      console.log('🔍 EDITING PRODUCT STATE CHECK:');
      console.log('  - editingProduct:', editingProduct);
      console.log('  - editingProduct exists:', !!editingProduct);
      console.log('  - editingProduct.id:', editingProduct?.id);
      console.log('  - editingProduct._id:', editingProduct?._id);
      console.log('  - editingProduct.productId:', editingProduct?.productId);
      console.log('  - Has any ID:', !!(editingProduct?.id || editingProduct?._id || editingProduct?.productId));
      
      // Force edit mode for testing
      if (editingProduct) {
        console.log('🔧 FORCING EDIT MODE - editingProduct exists, should update not create');
      } else {
        console.log('🔧 NO EDIT MODE - editingProduct is null/undefined, will create new');
      }
      
      console.log('✅ NO VALIDATION - All fields are optional, proceeding with product operation...');

      // Validate sale price must be less than original price
      const originalPrice = parseFloat(newProduct.originalPrice);
      const salePrice = parseFloat(newProduct.salePrice);
      
      if (newProduct.salePrice && !isNaN(salePrice) && !isNaN(originalPrice) && originalPrice > 0) {
        if (salePrice >= originalPrice) {
          showCustomAlert('Sale price must be less than original price. Please enter a lower sale price.', 'error');
          return;
        }
      }

      // Prepare product data for API according to documentation
      // Backend might require certain fields, so always send them (even if empty)
      const productData = {};
      
      // Basic fields - Always send (backend might require these)
      productData.name = newProduct.name ? newProduct.name.trim() : '';
      if (descriptionFormat === DESCRIPTION_FORMAT.HTML) {
        productData.description = sanitizeProductDescriptionHtml(newProduct.description || '');
        productData.descriptionRich = [];
      } else {
        productData.description = newProduct.description ? newProduct.description.trim() : '';
        productData.descriptionRich = [];
      }
      if (newProduct.shortDescription) productData.shortDescription = newProduct.shortDescription.trim();
      productData.category = newProduct.category || '';
      if (newProduct.subcategory) productData.subcategory = newProduct.subcategory.trim();
      if (newProduct.childrenCategory) productData.childrenCategory = newProduct.childrenCategory.trim();
      productData.brand = newProduct.brand ? newProduct.brand.trim() : '';
      if (newProduct.mainCategory && String(newProduct.mainCategory).trim()) {
        productData.mainCategory = String(newProduct.mainCategory).trim();
      }
      
      // Price fields — selling price in `price`; list price in `originalPrice` (storefront + API)
      const pricing = buildProductPricingFields(
        newProduct.originalPrice,
        newProduct.salePrice,
        { isOnSaleFlag: newProduct.isOnSale }
      );
      Object.assign(productData, pricing);
      productData.gender =
        newProduct.specifications?.gender ||
        newProduct.gender ||
        'Unisex';
      
      // Product identifiers
      if (newProduct.sku) productData.sku = newProduct.sku.trim();
      if (newProduct.url) productData.url = newProduct.url.trim();
      if (newProduct.productId) productData.productId = newProduct.productId.trim();
      
      // Arrays - Ensure they are arrays of strings
      if (newProduct.colors && Array.isArray(newProduct.colors) && newProduct.colors.length > 0) {
        // ✅ Ensure all colors are strings
        productData.colors = newProduct.colors.map(c => {
          if (typeof c === 'string') return c.trim();
          return String(c?.name || c?.color || c).trim();
        }).filter(c => c); // Remove empty strings
      }
      if (newProduct.sizes && Array.isArray(newProduct.sizes) && newProduct.sizes.length > 0) {
        // ✅ Ensure all sizes are strings
        productData.sizes = newProduct.sizes.map(s => {
          if (typeof s === 'string') return s.trim();
          return String(s?.name || s?.size || s).trim();
        }).filter(s => s); // Remove empty strings
      }
      // Single dimension: backend + storefront expect nested stock[color][size]; pad missing axis with `default`
      if (newProduct.colors?.length > 0 && newProduct.sizes?.length === 0) {
        productData.sizes = [SIMPLE_STOCK_SIZE];
      }
      if (newProduct.sizes?.length > 0 && newProduct.colors?.length === 0) {
        productData.colors = [SIMPLE_STOCK_COLOR];
      }
      // Backend rejects active saves when total stock is 0 — must send nested stock on PUT/POST (bulk runs after).
      if (newProduct.stock && typeof newProduct.stock === 'object' && Object.keys(newProduct.stock).length > 0) {
        productData.stock = buildStockPayloadForProductApi(newProduct.stock);
      }
      if (newProduct.tags && Array.isArray(newProduct.tags) && newProduct.tags.length > 0) {
        productData.tags = newProduct.tags;
      }
      if (newProduct.features && Array.isArray(newProduct.features) && newProduct.features.length > 0) {
        productData.features = newProduct.features;
      }
      
      // Images — lenient map so edits are not dropped when Cloudinary metadata is partial
      if (uploadedImages && uploadedImages.length > 0) {
        const apiImages = mapImagesForProductApi(uploadedImages);
        if (apiImages.length > 0) {
          productData.images = apiImages;
        }
      }
      
      // Specifications
      if (newProduct.specifications && Object.keys(newProduct.specifications).length > 0) {
        productData.specifications = newProduct.specifications;
      }
      
      // Status flags - Map status to isActive and status fields
      // Use finalStatus from button click (active for Add Product, draft for Draft button)
      console.log('🔍 Setting product status - finalStatus:', finalStatus);
      
      // Explicitly set status based on button clicked
      if (finalStatus === 'draft') {
        // Draft products are inactive - explicitly set both fields
        productData.isActive = false;
        productData.status = 'draft';
        // Also update the state to reflect draft status
        setNewProduct(prev => ({ ...prev, status: 'draft', isActive: false }));
        console.log('✅ Product set to DRAFT (inactive)');
      } else {
        // Default to active for Add Product button
        productData.isActive = true;
        productData.status = 'active';
        // Also update the state to reflect active status
        setNewProduct(prev => ({ ...prev, status: 'active', isActive: true }));
        console.log('✅ Product set to ACTIVE');
      }
      
      // Ensure status is never undefined
      if (!productData.status) {
        productData.status = finalStatus || 'active';
      }
      if (productData.isActive === undefined) {
        productData.isActive = finalStatus !== 'draft';
      }
      
      console.log('🔍 Final productData status:', {
        isActive: productData.isActive,
        status: productData.status,
        finalStatus: finalStatus
      });
      
      if (allowFeaturedProduct) {
        productData.isFeatured = Boolean(newProduct.isFeatured);
      } else {
        delete productData.isFeatured;
      }
      productData.isNew = Boolean(newProduct.isNew);
      productData.isOnSale = Boolean(newProduct.isOnSale);
      
      // Sale end date
      if (newProduct.saleEndDate) {
        productData.saleEndDate = newProduct.saleEndDate;
      }
      
      // Physical properties
      if (newProduct.weight) productData.weight = parseFloat(newProduct.weight) || undefined;
      if (newProduct.dimensions && Object.keys(newProduct.dimensions).length > 0) {
        productData.dimensions = newProduct.dimensions;
      }
      
      // Stock is included above for validation; bulk stock API still runs after save to normalize keys.
      
      // ✅ Ensure required fields are present (even if empty)
      // Backend might require these fields, so send them even if empty
      if (!productData.name) productData.name = newProduct.name || '';
      if (!productData.description && descriptionFormat !== DESCRIPTION_FORMAT.HTML) {
        productData.description = newProduct.description ? newProduct.description.trim() : '';
      }
      if (!productData.category) productData.category = newProduct.category || '';
      if (!productData.brand) productData.brand = newProduct.brand || '';
      if (productData.price === undefined || productData.price === null || Number.isNaN(productData.price)) {
        const fallback = buildProductPricingFields(newProduct.originalPrice, newProduct.salePrice);
        productData.price = fallback.price;
        productData.originalPrice = fallback.originalPrice;
      }
      
      console.log('🔍 Final productData being sent:', productData);
      console.log('🔍 ProductData keys:', Object.keys(productData));
      console.log('🔍 ProductData values:', Object.values(productData));
      console.log('🔍 Data types:', {
        name: typeof productData.name,
        description: typeof productData.description,
        category: typeof productData.category,
        brand: typeof productData.brand,
        sku: typeof productData.sku,
        url: typeof productData.url,
        productId: typeof productData.productId,
        originalPrice: typeof productData.originalPrice,
        price: typeof productData.price,
        subcategory: typeof productData.subcategory,
        colors: Array.isArray(productData.colors),
        sizes: Array.isArray(productData.sizes)
      });
      
      // Set createdBy for new products - backend uses JWT but we send as fallback for team members
      if (!editingProduct && currentUser?._id) {
        productData.createdBy = currentUser._id;
      } else if (!editingProduct && currentUser?.id) {
        productData.createdBy = currentUser.id;
      }

      let resultProduct;
      let savedMongoId = null;

      // Extract file objects from uploadedImages for new API
      console.log('📁 uploadedImages array:', uploadedImages);
      console.log('📁 uploadedImages length:', uploadedImages.length);
      
      const orderedForUpload = (() => {
        const list = [...uploadedImages];
        const pi = list.findIndex((i) => i.isPrimary);
        if (pi <= 0) return list;
        const copy = [...list];
        const [first] = copy.splice(pi, 1);
        return [first, ...copy];
      })();

      const imageFiles = orderedForUpload
        .filter((img) => img.file)
        .map((img) => img.file);
      
      console.log('📁 Extracted image files for API:', imageFiles.length);
      console.log('📁 imageFiles array:', imageFiles);
      console.log('📁 imageFiles details:', imageFiles.map(file => ({
        name: file?.name,
        size: file?.size,
        type: file?.type
      })));

      const editMongoId = getMongoProductId(editingProduct);
      const isEditMode = Boolean(editingProduct && editMongoId);

      // New product (multipart): send linkedColor / primary pin per file — backend merges into saved images[]
      if (!isEditMode && orderedForUpload.length > 0) {
        const withFiles = orderedForUpload.filter((img) => img.file);
        if (withFiles.length > 0) {
          productData.imagesMeta = withFiles.map((img) => {
            const meta = {};
            if (img.linkedColor && String(img.linkedColor).trim()) {
              meta.linkedColor = String(img.linkedColor).trim();
            }
            if (img.isPrimary) meta.isPrimary = true;
            return meta;
          });
        }
      }

      if (isEditMode) {
        const missingUpdate = getMissingFieldsForUpdate(productData);
        if (missingUpdate.length > 0) {
          showCustomAlert(formatMissingFieldsMessage(missingUpdate), 'error');
          return;
        }
      } else {
        const missingCreate = getMissingFieldsForCreate(productData, imageFiles);
        if (missingCreate.length > 0) {
          showCustomAlert(formatMissingFieldsMessage(missingCreate), 'error');
          return;
        }
      }

      if (getTotalStock() < 1) {
        showCustomAlert('Please set stock quantity to at least 1.', 'error');
        return;
      }

      if (isEditMode) {
        const productId = editMongoId;
        console.log('🔄 Updating product:', productId);

        if (!productId || !isMongoObjectId(productId)) {
          throw new Error(
            'Invalid product ID for update. Please close and open the product again from the products list.'
          );
        }
        
        try {
          resultProduct = await updateProduct(productId, productData);
          console.log('✅ Product updated successfully:', resultProduct);
          savedMongoId = productId;
        } catch (updateError) {
          console.error('❌ Error updating product:', updateError);
          throw updateError;
        }

        if (pendingVideo?.file) {
          try {
            await productService.uploadProductVideo(productId, pendingVideo.file);
            console.log('✅ Product video uploaded');
          } catch (vidErr) {
            console.error('❌ Video upload failed:', vidErr);
            showCustomAlert(
              (vidErr && vidErr.message) || 'Product saved but video upload failed. Try again or check backend.',
              'warning'
            );
          }
        }

        // Update stock using bulk stock API if stock data exists
        if (productId && newProduct.stock && Object.keys(newProduct.stock).length > 0) {
          try {
            // Prepare stock updates array from stock object
            const stockUpdates = [];
            Object.keys(newProduct.stock).forEach((color) => {
              Object.keys(newProduct.stock[color]).forEach((size) => {
                const quantity = parseInt(newProduct.stock[color][size], 10) || 0;
                if (quantity > 0) {
                  stockUpdates.push({
                    color: String(color).trim(),
                    size: String(size).trim(),
                    quantity,
                  });
                }
              });
            });
            
            if (stockUpdates.length > 0) {
              console.log('📦 Updating stock using bulk stock API:', stockUpdates);
              await productService.bulkUpdateStock(productId, stockUpdates);
              console.log('✅ Stock updated successfully using bulk stock API');
            }
          } catch (stockError) {
            console.error('❌ Error updating stock:', stockError);
            // Don't throw error - product was updated successfully, stock can be updated later
            showCustomAlert('Product updated but stock update failed. Please update stock manually.', 'warning');
          }
        }
      } else {
        // Create new product
        console.log('➕ Creating new product...');
        console.log('➕ Edit mode not detected - creating new product');
        console.log('➕ editingProduct:', editingProduct);
        console.log('➕ editingProduct.id:', editingProduct?.id);
        console.log('➕ editingProduct._id:', editingProduct?._id);
        console.log('➕ editingProduct.productId:', editingProduct?.productId);
        console.log('➕ Condition check: editingProduct && (editingProduct.id || editingProduct._id || editingProduct.productId)');
        console.log('➕ Result:', !!(editingProduct && (editingProduct.id || editingProduct._id || editingProduct.productId)));
        
        console.log('➕ About to call createProduct with:');
        console.log('➕ productData:', productData);
        console.log('➕ imageFiles:', imageFiles);
        console.log('➕ imageFiles.length:', imageFiles.length);
        
        try {
          resultProduct = await createProduct(
            productData,
            imageFiles,
            pendingVideo?.file || null
          );
          console.log('✅ Product created successfully:', resultProduct);

          const createdProductId = extractCreatedProductId(resultProduct);
          savedMongoId = createdProductId;

          const afterCreatePricing = buildProductPricingFields(
            newProduct.originalPrice,
            newProduct.salePrice,
            { isOnSaleFlag: newProduct.isOnSale }
          );

          if (createdProductId && afterCreatePricing.isOnSale) {
            try {
              const synced = await updateProductPricing(createdProductId, {
                originalPrice: newProduct.originalPrice,
                salePrice: newProduct.salePrice,
                isOnSale: true,
              });
              if (synced && typeof synced === 'object') {
                resultProduct = { ...resultProduct, ...synced };
              }
            } catch (pricingErr) {
              console.warn('Post-create sale pricing sync skipped:', pricingErr?.message || pricingErr);
            }
          }
          
          // Save stock using bulk stock API if stock data exists
          if (createdProductId && newProduct.stock && Object.keys(newProduct.stock).length > 0) {
            try {
              // Prepare stock updates array from stock object
              const stockUpdates = [];
              Object.keys(newProduct.stock).forEach((color) => {
                Object.keys(newProduct.stock[color]).forEach((size) => {
                  const quantity = parseInt(newProduct.stock[color][size], 10) || 0;
                  if (quantity > 0) {
                    stockUpdates.push({
                      color: String(color).trim(),
                      size: String(size).trim(),
                      quantity,
                    });
                  }
                });
              });
              
              if (stockUpdates.length > 0) {
                console.log('📦 Saving stock using bulk stock API:', stockUpdates);
                await productService.bulkUpdateStock(createdProductId, stockUpdates);
                console.log('✅ Stock saved successfully using bulk stock API');
              }
            } catch (stockError) {
              console.error('❌ Error saving stock:', stockError);
              // Don't throw error - product was created successfully, stock can be updated later
              showCustomAlert('Product created but stock update failed. Please update stock manually.', 'warning');
            }
          }
        } catch (createError) {
          console.error('❌ Create product error details:', createError);
          console.error('❌ Error message:', createError.message);
          console.error('❌ Error stack:', createError.stack);
          throw createError;
        }
      }

      if (savedMongoId) {
        try {
          const syncedFlags = await syncProductStatusFlagsAfterSave(
            savedMongoId,
            newProduct,
            { getApiUrl, getAdminToken },
            { includeFeatured: allowFeaturedProduct }
          );
          if (syncedFlags && resultProduct && typeof resultProduct === 'object') {
            resultProduct = { ...resultProduct, ...syncedFlags };
          }
        } catch (statusErr) {
          console.error('❌ Product status flags sync failed:', statusErr);
          const wantedFlags =
            (allowFeaturedProduct && newProduct.isFeatured) ||
            newProduct.isNew ||
            newProduct.isOnSale;
          if (wantedFlags) {
            showCustomAlert(
              (statusErr && statusErr.message) ||
                'Product saved but New / Sale flags could not be applied. Use toggles on the Products list or edit again.',
              'warning'
            );
          }
        }
      } else if (
        !editingProduct &&
        ((allowFeaturedProduct && newProduct.isFeatured) || newProduct.isNew || newProduct.isOnSale)
      ) {
        showCustomAlert(
          'Product was created but we could not read its ID to save New / Sale flags. Open the product from the list and set them again.',
          'warning'
        );
      }

             // Set success data and show golden popup
             console.log('🎉 Product operation successful, result data:', resultProduct);
             console.log('🔍 Product URL field:', resultProduct?.url);
             console.log('🔍 Product ID fields:', {
               id: resultProduct?.id,
               _id: resultProduct?._id,
               productId: resultProduct?.productId
             });
             
             console.log('🎯 Setting success popup data...');
             const pricedForDisplay = normalizeProductPricesForDisplay({
               ...(resultProduct && typeof resultProduct === 'object' ? resultProduct : {}),
               originalPrice:
                 resultProduct?.originalPrice ??
                 productData.originalPrice ??
                 productData.compareAtPrice,
               price: resultProduct?.price ?? productData.price,
               salePrice: resultProduct?.salePrice ?? productData.salePrice,
               isOnSale: resultProduct?.isOnSale ?? productData.isOnSale,
             });
             setNewProductData(
               buildGoldenSuccessProductState(
                 resultProduct,
                 newProduct,
                 uploadedImages,
                 pricedForDisplay
               )
             );
             console.log('🎯 Setting showGoldenSuccess to true...');
             setShowGoldenSuccess(true);
             setSaved(true);

             try {
               await refetchProducts();
             } catch (refetchErr) {
               console.warn('Products list refresh after save:', refetchErr);
             }
             
             // Debug: Check if state was set correctly
             setTimeout(() => {
               console.log('🔍 Debug - showGoldenSuccess state after setting:', showGoldenSuccess);
               console.log('🔍 Debug - newProductData state after setting:', newProductData);
             }, 100);
      
      // Clear editing product and reset form after success
      setTimeout(
        () => {
          console.log('🔄 Clearing editing product after successful operation');
          if (onEditComplete) {
            onEditComplete();
          }
          resetForm();
        },
        variantHubOnly ? 1200 : 3000
      ); // Hub modal: shorter delay so the list refreshes and modal closes soon after success
      
    } catch (error) {
      console.error('❌ Error saving product:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Extract error message properly
      let errorMessage = `Error ${editingProduct ? 'updating' : 'adding'} product. Please try again.`;
      
      // Try to parse error message if it's a JSON string
      try {
        const errorObj = typeof error.message === 'string' ? JSON.parse(error.message) : error.message;
        if (errorObj && typeof errorObj === 'object') {
          if (errorObj.error) errorMessage = errorObj.error;
          else if (errorObj.message) errorMessage = errorObj.message;
          else if (errorObj.errors && Array.isArray(errorObj.errors)) {
            errorMessage = errorObj.errors
              .map((e) => {
                const msg = e.msg || e.message || String(e);
                const field = e.param || e.path;
                return field ? `${field}: ${msg}` : msg;
              })
              .join(', ');
          }
        } else if (typeof errorObj === 'string') {
          errorMessage = errorObj;
        }
      } catch (e) {
        // Not a JSON string, use error.message directly
        if (error.message) {
          errorMessage = error.message;
        }
      }
      
      // More specific error messages
      if (errorMessage.includes('Product not found') || errorMessage.includes('not found')) {
        errorMessage = `Product not found for updating. This might be because the product was deleted or the ID is incorrect. Please try refreshing the page and try again.`;
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        errorMessage = `Authentication failed. Please login again and try.`;
      } else if (errorMessage.includes('404')) {
        errorMessage = `Product endpoint not found. Please verify backend route: PUT /api/admin/products/:id`;
      }
      
      console.error('❌ Final error message:', errorMessage);
      showCustomAlert(errorMessage, 'error');
    } finally {
      // Loading state is now handled by hooks
    }
  };

  const resetForm = () => {
    setWizardStep(0);
    setDescriptionFormat(DESCRIPTION_FORMAT.HTML);
    setDescLoadNonce((n) => n + 1);
    setNewProduct({ ...EMPTY_NEW_PRODUCT });
    setUploadedImages([]);
    if (pendingVideo?.previewUrl) {
      URL.revokeObjectURL(pendingVideo.previewUrl);
    }
    setPendingVideo(null);
    setExistingVideoUrl('');
    if (productVideoInputRef.current) {
      productVideoInputRef.current.value = '';
    }
    setCustomColorPalette([]);
    setHexDraftByImage({});
    // Don't clear success popup state here - let it stay visible
    // setShowGoldenSuccess(false);
    // setNewProductData(null);
  };

  const isSimpleStockMode =
    newProduct.colors.length === 0 && newProduct.sizes.length === 0;
  const isColorOnlyStockMode =
    newProduct.colors.length > 0 && newProduct.sizes.length === 0;
  const isSizeOnlyStockMode =
    newProduct.colors.length === 0 && newProduct.sizes.length > 0;
  const isMatrixStockMode =
    newProduct.colors.length > 0 && newProduct.sizes.length > 0;

  const wizardStepCount = ADD_PRODUCT_WIZARD_STEPS.length;
  const wizardStepMeta = ADD_PRODUCT_WIZARD_STEPS[wizardStep] || ADD_PRODUCT_WIZARD_STEPS[0];
  const wizardCtx = useMemo(
    () => ({ newProduct, uploadedImages, getTotalStock }),
    [newProduct, uploadedImages, getTotalStock]
  );
  const wizardStepComplete = isAddProductWizardStepComplete(wizardStep, wizardCtx);
  const showWizardNext = canShowWizardNextButton(wizardStep, wizardCtx);
  const isLastWizardStep = wizardStep >= wizardStepCount - 1;

  const goWizardBack = () => {
    if (wizardStep > 0) {
      setWizardStep((s) => Math.max(0, s - 1));
      return;
    }
    if (onNavigate) onNavigate('products');
  };

  const goWizardNext = () => {
    const missing = getAddProductWizardStepMissingFields(wizardStep, wizardCtx);
    if (missing.length > 0) {
      showCustomAlert(formatWizardStepMissingMessage(wizardStep, missing), 'error');
      return;
    }
    if (isLastWizardStep) {
      handleAddProduct();
      return;
    }
    setWizardStep((s) => Math.min(wizardStepCount - 1, s + 1));
  };

  const saveWizardDraft = () => {
    handleAddProduct('draft');
  };

  return (
    <div
      className={`add-product-container${variantHubOnly ? ' add-product-variant-hub-only' : ''}`}
    >
      {/* ✅ Admin authentication requirement removed - form always accessible */}
      <div>

          {/* Custom Alert */}
      {showAlert && (
        <div className={`custom-alert ${alertType} ${alertType === 'error' ? 'golden-alert-theme' : ''}`} style={{zIndex: 10000}}>
          {alertType === 'error' ? (
            <div className="golden-alert">
              <div className="golden-alert-content">
                <div className="golden-alert-header">
                  <div className="golden-alert-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <div className="golden-alert-title">Error</div>
                </div>
                <div className="golden-alert-message">
                  <div className="alert-text-line">
                    {alertMessage}
                  </div>
                </div>
                <button className="golden-alert-close" onClick={() => setShowAlert(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          ) : (
            <div className="alert-content">
              <div className="alert-icon">
                {alertType === 'error' && <i className="fas fa-exclamation-circle"></i>}
                {alertType === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {alertType === 'success' && <i className="fas fa-check-circle"></i>}
              </div>
              <div className="alert-message">
                {alertMessage}
              </div>
              <button className="alert-close" onClick={() => setShowAlert(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
          {alertType !== 'error' && <div className="alert-progress"></div>}
        </div>
      )}

      {/* Golden Success Popup */}
      {showGoldenSuccess && newProductData && (
        <div
          className="golden-success-overlay"
          onClick={() => setShowGoldenSuccess(false)}
        >
          <div className="golden-success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="golden-success-header">
              <div className="golden-success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>{editingProduct ? 'Product Updated Successfully!' : 'Product Added Successfully!'}</h2>
              <p>{editingProduct ? 'Your product has been updated and saved.' : 'Your new product has been created and saved.'}</p>
            </div>
            
            <div className="golden-success-content">
              <div className="product-preview">
                <div
                  className={`product-image${
                    newProductData._previewImageUrl ? '' : ' product-image--empty'
                  }`}
                >
                  {newProductData._previewImageUrl ? (
                    <img
                      src={newProductData._previewImageUrl}
                      alt={newProductData.name || 'Product'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.closest('.product-image')?.classList.add('product-image--empty');
                      }}
                    />
                  ) : null}
                  <div className="no-image" aria-hidden={!!newProductData._previewImageUrl}>
                    <i className="fas fa-image" />
                  </div>
                </div>
                <div className="product-details">
                  <h3>{newProductData.name || 'Product Name'}</h3>
                  <p className="product-category">
                    {newProductData._previewCategoryLabel || 'Category'}
                  </p>
                  <p className="product-price">
                    {(() => {
                      const p = normalizeProductPricesForDisplay(newProductData);
                      if (p.isOnSale && p.originalPrice > p.price) {
                        return (
                          <>
                            <span className="sale-price">Sale: PKR {p.price.toLocaleString()}</span>
                            {' · '}
                            <span className="list-price">Real: PKR {p.originalPrice.toLocaleString()}</span>
                          </>
                        );
                      }
                      return <>PKR {(p.price || 0).toLocaleString()}</>;
                    })()}
                  </p>
                  <p className="product-id">SKU: {newProductData.sku || 'N/A'}</p>
                  <p className="product-url">
                    <span className="product-url-label">URL:</span>{' '}
                    <span className="product-url-value">
                      {newProductData._previewPublicUrl || 'N/A'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="golden-success-actions">
              <button 
                className="golden-btn primary"
                onClick={() => {
                  const productUrl =
                    newProductData._previewPublicUrl || buildPublicProductPageUrl(newProductData);
                  if (productUrl && productUrl.includes(window.location.origin)) {
                    window.open(productUrl, '_blank');
                  } else {
                    alert('Product link not available. Please open it from the products list.');
                  }
                }}
                title="Open product in new tab"
              >
                <i className="fas fa-external-link-alt"></i>
                Open Product Link
              </button>
              <button 
                className="golden-btn secondary"
                onClick={() => {
                  setShowGoldenSuccess(false);
                  onNavigate('products');
                }}
              >
                <i className="fas fa-eye"></i>
                View All Products
              </button>
              <button 
                className="golden-btn tertiary"
                onClick={() => {
                  setShowGoldenSuccess(false);
                  resetForm();
                }}
              >
                <i className="fas fa-plus"></i>
                {editingProduct ? 'Edit Another Product' : 'Add Another Product'}
              </button>
              <button 
                className="golden-btn close-btn"
                onClick={() => setShowGoldenSuccess(false)}
              >
                <i className="fas fa-times"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Header */}
      <div className="add-product-header">
        <div className="header-left">
          <button 
            className="back-btn"
            onClick={() => onNavigate('products')}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Products
          </button>
          <h1>{editingProduct ? 'Edit Product' : 'Add New Product'}</h1>
          <p className="add-product-subtitle">
            {editingProduct ? 'Update product information' : 'Create a new product for your store'}
          </p>
          {!editingProduct && newProduct.sku && (
            <div style={{ 
              background: 'linear-gradient(135deg, #2196F3, #1976D2)', 
              color: 'white', 
              padding: '8px 12px', 
              borderRadius: '5px', 
              fontSize: '12px', 
              fontWeight: '600',
              marginTop: '8px',
              textAlign: 'center',
              fontFamily: 'Courier New, monospace'
            }}>
              🔢 Generated SKU: {newProduct.sku}
            </div>
          )}
          
        </div>
        <div className="header-right add-product-header-desktop-actions">
          <button 
            className={`btn-primary ${saved ? 'saved' : ''}`}
            onClick={handleAddProduct}
            disabled={creating || updating}
          >
            {(creating || updating) ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {editingProduct ? 'Updating Product...' : 'Adding Product...'}
              </>
            ) : saved ? (
              <>
                <i className="fas fa-check"></i>
                {editingProduct ? 'Product Updated!' : 'Product Added!'}
              </>
            ) : (
              <>
                <i className={editingProduct ? 'fas fa-save' : 'fas fa-plus'}></i>
                {editingProduct ? 'Update Product' : 'Add Product'}
              </>
            )}
          </button>
        </div>
      </div>

      {useProductWizard && (
        <div className="add-product-wizard-progress" aria-label="Product form steps">
          <div className="add-product-wizard-progress__meta">
            <span className="add-product-wizard-progress__count">
              Step {wizardStep + 1} of {wizardStepCount}
            </span>
            <strong className="add-product-wizard-progress__title">{wizardStepMeta.title}</strong>
            <p className="add-product-wizard-progress__subtitle">{wizardStepMeta.subtitle}</p>
          </div>
          <div className="add-product-wizard-progress__dots">
            {ADD_PRODUCT_WIZARD_STEPS.map((step, idx) => (
              <button
                key={step.id}
                type="button"
                className={`add-product-wizard-dot${idx === wizardStep ? ' is-active' : ''}${idx < wizardStep ? ' is-done' : ''}`}
                onClick={() => {
                  if (idx <= wizardStep || editingProduct) setWizardStep(idx);
                }}
                title={step.title}
                aria-label={`${step.title}${idx === wizardStep ? ' (current)' : ''}`}
                aria-current={idx === wizardStep ? 'step' : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Product Form */}
      <div className={`product-form${useProductWizard ? ' product-form--wizard' : ''}`}>
        <div className="form-sections">
          {/* Step 1 — Name, SKU, pricing (wizard step index 1) */}
          {(!useProductWizard || wizardStep === 1) && (
          <div className="form-section wizard-step-panel">
            <div className="section-header">
              <h3>{useProductWizard ? 'Product name & pricing' : 'Basic Information'}</h3>
              <p>{useProductWizard ? 'Enter name, SKU and prices' : 'Enter the basic details of your product'}</p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Product Name</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter product name"
                  />
                </div>
                <div className="form-group">
                  <label>Original Price</label>
                  <input
                    type="number"
                    value={newProduct.originalPrice ?? ''}
                    onChange={(e) => handleInputChange('originalPrice', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Sale Price</label>
                  <input
                    type="number"
                    value={newProduct.salePrice ?? ''}
                    onChange={(e) => handleInputChange('salePrice', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    style={{
                      borderColor: (() => {
                        const originalPrice = parseFloat(newProduct.originalPrice);
                        const salePrice = parseFloat(newProduct.salePrice);
                        if (newProduct.salePrice && !isNaN(salePrice) && !isNaN(originalPrice) && originalPrice > 0) {
                          if (salePrice >= originalPrice) {
                            return '#dc3545';
                          }
                        }
                        return undefined;
                      })()
                    }}
                  />
                  {(() => {
                    const originalPrice = parseFloat(newProduct.originalPrice);
                    const salePrice = parseFloat(newProduct.salePrice);
                    if (newProduct.salePrice && !isNaN(salePrice) && !isNaN(originalPrice) && originalPrice > 0) {
                      if (salePrice >= originalPrice) {
                        return (
                          <div className="field-error">
                            <i className="fas fa-exclamation-circle"></i>
                            Sale price must be less than original price (PKR {originalPrice.toLocaleString()})
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
                <div className="form-group">
                  <label>SKU (Stock Keeping Unit)</label>
                  <div className="sku-field-container">
                    <input
                      type="text"
                      value={newProduct.sku}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      placeholder="Enter SKU (e.g., SW-001)"
                      required
                      className="sku-input"
                    />
                    {!editingProduct && (
                      <div className="sku-controls">
                        <label className="auto-sku-toggle">
                          <input
                            type="checkbox"
                            checked={autoGenerateSKU}
                            onChange={(e) => {
                              setAutoGenerateSKU(e.target.checked);
                              if (e.target.checked && newProduct.category) {
                                generateNewSKU(newProduct.category);
                              }
                            }}
                          />
                          <span>Auto Generate</span>
                        </label>
                        <button
                          type="button"
                          className="generate-sku-btn"
                          onClick={() => generateNewSKU(newProduct.name)}
                          disabled={!newProduct.name}
                        >
                          <i className="fas fa-sync-alt"></i>
                          Generate SKU
                        </button>
                      </div>
                    )}
                  </div>
                  {skuPreview && autoGenerateSKU && !editingProduct && (
                    <div className="sku-preview">
                      <small>Preview: {skuPreview}</small>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>SEO-Friendly URL</label>
                  <input
                    type="text"
                    value={newProduct.url}
                    onChange={(e) => handleInputChange('url', e.target.value)}
                    placeholder="creator-product-name-id"
                    readOnly={autoGenerateSKU && !editingProduct}
                    className={autoGenerateSKU && !editingProduct ? 'readonly-field' : ''}
                  />
                  <small className="field-hint">
                    Format: your shop URL + product name + product ID (e.g. umardaraz-cotton-shirt-00050)
                  </small>
                </div>
                <div className="form-group">
                  <label>Product ID</label>
                  <input
                    type="text"
                    value={newProduct.productId}
                    onChange={(e) => handleInputChange('productId', e.target.value)}
                    placeholder="Auto-generated from SKU"
                    readOnly={autoGenerateSKU && !editingProduct}
                    className={autoGenerateSKU && !editingProduct ? 'readonly-field' : ''}
                  />
                  <small className="field-hint">Product ID will be auto-generated from SKU</small>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 0 — Categories (wizard) */}
          {(!useProductWizard || wizardStep === 0) && (
          <div className="form-section wizard-step-panel">
            <div className="section-header">
              <h3>Categories</h3>
              <p>Select primary category, subcategory and children category</p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Primary Category</label>
                  <CategorySelector
                    onCategoryChange={handleCategoryChange}
                    onMainCategoryDisplayName={(name) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        mainCategory: name || '',
                      }))
                    }
                    selectedCategory={newProduct.category ?? ''}
                    selectedSubcategory={newProduct.subcategory ?? ''}
                    onSubcategoryChange={handleSubcategoryChange}
                    selectedChildrenCategory={newProduct.childrenCategory ?? ''}
                    onChildrenCategoryChange={(childrenCategoryId) => {
                      setNewProduct((prev) => ({
                        ...prev,
                        childrenCategory: childrenCategoryId ?? '',
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 4 — Brand & material (wizard) / inline when not wizard */}
          {(!useProductWizard || wizardStep === 4) && (
          <div className="form-section wizard-step-panel">
            <div className="section-header">
              <h3>Brand &amp; material</h3>
              <p>Brand name and fabric / style tags</p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-group">
                  <label>Brand</label>
                  {existingBrandsFromCatalog.length > 0 && (
                    <div className="catalog-pick-block">
                      <span className="catalog-pick-label">Already used brands</span>
                      <div className="catalog-pick-chips">
                        {existingBrandsFromCatalog.map((b) => {
                          const active =
                            String(newProduct.brand || '')
                              .trim()
                              .toLowerCase() === b.toLowerCase();
                          return (
                            <button
                              key={b}
                              type="button"
                              className={`catalog-pick-chip ${active ? 'catalog-pick-chip--active' : ''}`}
                              onClick={() => handleInputChange('brand', b)}
                              title={active ? 'Selected' : `Use “${b}”`}
                            >
                              {b}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <input
                    type="text"
                    value={newProduct.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    placeholder="Enter brand name"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Material</label>
                  <div className="material-ai-selection" style={{ marginBottom: '12px' }}>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#5b21b6',
                        display: 'block',
                        marginBottom: '4px',
                        fontWeight: '600',
                      }}
                    >
                      <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '6px' }} />
                      AI selection
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#6c757d',
                        display: 'block',
                        marginBottom: '8px',
                      }}
                    >
                      Detected from your product description (up to 20 tags)
                    </span>
                    {!descriptionPlainForMaterials ? (
                      <p className="material-ai-selection__empty">
                        Add a description in the previous step to see AI material suggestions here.
                      </p>
                    ) : aiMaterialSuggestions.length === 0 ? (
                      <p className="material-ai-selection__empty">
                        No fabric or style keywords found in the description yet. Mention materials like Cotton, Lawn, Silk, etc.
                      </p>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          alignItems: 'center',
                        }}
                      >
                        {aiMaterialSuggestions.map((label) => {
                          const isAdded = (newProduct.tags || []).includes(label);
                          return (
                            <button
                              key={`ai-${label}`}
                              type="button"
                              onClick={() => handleQuickMaterialClick(label)}
                              title={isAdded ? `Remove ${label}` : `Add ${label} from description`}
                              className="material-ai-selection__chip"
                              style={{
                                padding: '6px 10px',
                                fontSize: '12px',
                                fontWeight: '600',
                                borderRadius: '8px',
                                border: `1px solid ${isAdded ? '#28a745' : '#c4b5fd'}`,
                                background: isAdded ? '#d4edda' : 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                                color: isAdded ? '#155724' : '#5b21b6',
                                cursor: 'pointer',
                                opacity: isAdded ? 0.85 : 1,
                                transition: 'background 0.15s, border-color 0.15s',
                              }}
                            >
                              {isAdded ? '✓ ' : ''}
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                      }}
                    >
                      Quick select
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        alignItems: 'center',
                      }}
                    >
                      {MATERIAL_QUICK_TAGS.map((label) => {
                        const isAdded = (newProduct.tags || []).includes(label);
                        return (
                          <button
                            key={label}
                            type="button"
                            onClick={() => handleQuickMaterialClick(label)}
                            title={isAdded ? `Remove ${label}` : `Add ${label}`}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              fontWeight: '600',
                              borderRadius: '8px',
                              border: `1px solid ${isAdded ? '#28a745' : '#dee2e6'}`,
                              background: isAdded ? '#d4edda' : '#fff',
                              color: isAdded ? '#155724' : '#495057',
                              cursor: 'pointer',
                              opacity: isAdded ? 0.85 : 1,
                              transition: 'background 0.15s, border-color 0.15s',
                            }}
                          >
                            {isAdded ? '✓ ' : ''}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {existingMaterialTagsFromCatalog.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6c757d',
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                        }}
                      >
                        Already used materials
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          alignItems: 'center',
                        }}
                      >
                        {existingMaterialTagsFromCatalog.map((label) => {
                          const isAdded = (newProduct.tags || []).includes(label);
                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => handleQuickMaterialClick(label)}
                              title={isAdded ? `Remove ${label}` : `Add ${label}`}
                              style={{
                                padding: '6px 10px',
                                fontSize: '12px',
                                fontWeight: '600',
                                borderRadius: '8px',
                                border: `1px solid ${isAdded ? '#28a745' : '#c5a059'}`,
                                background: isAdded ? '#d4edda' : '#fffbf0',
                                color: isAdded ? '#155724' : '#6b4e16',
                                cursor: 'pointer',
                                opacity: isAdded ? 0.85 : 1,
                                transition: 'background 0.15s, border-color 0.15s',
                              }}
                            >
                              {isAdded ? '✓ ' : ''}
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="tags-input-container" style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={tagsInput}
                        onChange={(e) => {
                          handleTagsInputChange(e.target.value);
                          setShowSubcategorySuggestions(true);
                        }}
                        onKeyPress={handleTagInputKeyPress}
                        onFocus={() => setShowSubcategorySuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSubcategorySuggestions(false), 200)}
                        placeholder="e.g. Cotton, Denim — press Enter or Add"
                        className="tags-input"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        disabled={!tagsInput.trim()}
                        className="btn-add-tag"
                        style={{
                          padding: '12px 20px',
                          background: tagsInput.trim() ? 'linear-gradient(135deg, #007bff, #0056b3)' : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: tagsInput.trim() ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          whiteSpace: 'nowrap',
                          opacity: tagsInput.trim() ? 1 : 0.6
                        }}
                        onMouseEnter={(e) => {
                          if (tagsInput.trim()) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>
                        Add
                      </button>
                    </div>
                    {showSubcategorySuggestions && availableSubcategories.length > 0 && (
                      <div className="subcategory-suggestions" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6c757d', borderBottom: '1px solid #e9ecef' }}>
                          Subcategories (click to add as material):
                        </div>
                        {availableSubcategories.map((subcategory, index) => (
                          <div
                            key={index}
                            onClick={() => handleSubcategorySuggestionClick(subcategory)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: index < availableSubcategories.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <i className="fas fa-layer-group" style={{ marginRight: '8px', color: '#007bff', fontSize: '12px' }}></i>
                            {subcategory}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Material chips (stored as tags in API) */}
                  {newProduct.tags && newProduct.tags.length > 0 && (
                    <div style={{ 
                      marginTop: '12px', 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '8px' 
                    }}>
                      {newProduct.tags.map((tag, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #007bff, #0056b3)',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: '500',
                            boxShadow: '0 2px 6px rgba(0, 123, 255, 0.3)'
                          }}
                        >
                          <i className="fas fa-layer-group" style={{ fontSize: '10px' }}></i>
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            style={{
                              background: 'rgba(255, 255, 255, 0.3)',
                              border: 'none',
                              color: 'white',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '10px',
                              padding: 0,
                              marginLeft: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.5)';
                              e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                              e.target.style.transform = 'scale(1)';
                            }}
                            title="Remove material"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <small className="field-hint" style={{ display: 'block', marginTop: '8px', color: '#6c757d' }}>
                    {newProduct.tags && newProduct.tags.length > 0
                      ? `${newProduct.tags.length} material value(s) added. Use AI selection, quick select, already used materials, type below, or subcategory suggestions.`
                      : 'Use AI selection from description, quick select, already used materials, type fabric/material, or pick from subcategories when shown.'}
                  </small>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 3 — Description */}
          {(!useProductWizard || wizardStep === 3) && (
          <div className="form-section wizard-step-panel">
            <div className="section-header">
              <h3>Description</h3>
              <p>Product details for shoppers and search</p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Description</label>
                  <ProductDescriptionHtmlEditor
                    format={descriptionFormat}
                    onFormatChange={handleDescriptionFormatChange}
                    value={newProduct.description}
                    onChange={(v) => handleInputChange('description', v)}
                    plainMaxLength={2000}
                    editorKey={descriptionEditorKey}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 2 / 5 — Photos & video (step 2) · colors & stock (step 5) */}
          {(!useProductWizard || wizardStep === 2 || wizardStep === 5) && (
          <div className="form-section variant-inventory-hub wizard-step-panel">
            <div className="section-header">
              <h3>
                {useProductWizard
                  ? wizardStep === 5
                    ? 'Colors, sizes & stock'
                    : 'Photos & video'
                  : 'Photos, variant colors & stock'}
              </h3>
            </div>
            <div className="section-content variant-inventory-hub__body">
              {(!useProductWizard || wizardStep === 2) && (
              <div className="vih-card vih-card--photos">
                <h4 className="vih-card__title">
                  <i className="fas fa-images" aria-hidden="true" /> Product photos
                </h4>
                <p className="vih-card__hint">
                  The first image is the default <strong>card image</strong> — use the pin on any thumbnail to change it.
                  Link a variant color to each photo so shoppers see the right shot per color (optional).
                </p>
                <div className="image-upload-area">
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="image-upload" className="upload-btn">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Select images</span>
                    <small>PNG, JPG, GIF up to 10MB each · uploaded when you save</small>
                  </label>
                </div>

                {photoColorPick && (
                  <div className="vih-photo-color-pick-banner" role="status">
                    <span>
                      {photoColorPick.scope === 'image'
                        ? 'Tap this photo to sample a color for its variant link.'
                        : 'Tap any product photo below to add that color.'}
                    </span>
                    <button type="button" className="vih-photo-color-pick-banner__cancel" onClick={cancelPhotoColorPick}>
                      Cancel
                    </button>
                  </div>
                )}

                {uploadedImages.length > 0 && (
                  <div className="uploaded-images">
                    <h4>Images ({uploadedImages.length})</h4>
                    <div
                      className={`images-grid images-grid--variant-bind${
                        photoColorPick ? ' images-grid--color-pick-mode' : ''
                      }`}
                    >
                      {uploadedImages.map((image, index) => {
                        const colorQuery =
                          imageColorInput[index] !== undefined
                            ? imageColorInput[index]
                            : image.linkedColor || '';
                        const colorSuggestions = filterColorsByQuery(
                          searchableColorsForImageLink,
                          colorQuery
                        );
                        const isPhotoPickTarget =
                          photoColorPick &&
                          (photoColorPick.scope === 'global' ||
                            (photoColorPick.scope === 'image' && photoColorPick.index === index));
                        return (
                        <div
                          key={`upl-img-${index}`}
                          className={`image-preview${image.isPrimary ? ' image-preview--primary' : ''}${
                            isPhotoPickTarget ? ' image-preview--color-pick' : ''
                          }`}
                        >
                          <div className="image-preview__frame">
                            <img
                              src={image.previewUrl || image.url}
                              alt={`Preview ${index + 1}`}
                              crossOrigin={
                                (() => {
                                  const src = image.previewUrl || image.url || '';
                                  if (!src || src.startsWith('blob:') || src.startsWith('data:')) {
                                    return undefined;
                                  }
                                  return 'anonymous';
                                })()
                              }
                              onPointerUp={
                                photoColorPick ? (e) => handleProductPhotoColorSample(index, e) : undefined
                              }
                            />
                            {image.isPrimary && (
                              <span className="image-primary-badge">
                                <i className="fas fa-thumbtack" aria-hidden="true" />
                                Card image
                              </span>
                            )}
                            <button
                              type="button"
                              className="image-pin-btn"
                              onClick={() => setPrimaryUploadedImage(index)}
                              title="Show this image on product cards and listing"
                            >
                              <i className="fas fa-thumbtack" aria-hidden="true" />
                            </button>
                            <button
                              className="remove-image"
                              type="button"
                              onClick={() => removeImage(index)}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                          <div className="image-info">
                            <span className="image-name">{image.name || 'Image'}</span>
                            {image.size != null && (
                              <span className="image-size">{(image.size / 1024).toFixed(1)} KB</span>
                            )}
                          </div>
                          <div className="image-variant-bind">
                            <div className="image-variant-bind__label-row">
                              <label htmlFor={`img-color-${index}`}>Variant color for this photo</label>
                              <button
                                type="button"
                                className="image-variant-bind__clear"
                                onClick={() => {
                                  setImageLinkedColor(index, '');
                                  setImageColorInput((prev) => ({ ...prev, [index]: '' }));
                                }}
                              >
                                Clear
                              </button>
                            </div>
                            <div className="image-variant-bind__typeahead">
                              <input
                                id={`img-color-${index}`}
                                type="text"
                                className="image-variant-bind__input"
                                placeholder="Type color name…"
                                autoComplete="off"
                                spellCheck={false}
                                value={colorQuery}
                                onChange={(e) =>
                                  handleImageColorFieldChange(index, e.target.value)
                                }
                                onFocus={() => handleImageColorFieldFocus(index)}
                                onBlur={() => handleImageColorFieldBlur(index)}
                              />
                              {openImageColorSuggest === index &&
                                colorSuggestions.length > 0 && (
                                  <ul className="image-variant-bind__suggestions">
                                    {colorSuggestions.map((c) => (
                                      <li
                                        key={c.name}
                                        className="image-variant-bind__suggestion"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() =>
                                          pickImageColorSuggestion(index, c.name)
                                        }
                                      >
                                        <span
                                          className="image-variant-bind__swatch"
                                          style={{ backgroundColor: c.code }}
                                        />
                                        <span className="image-variant-bind__suggestion-name">
                                          {normalizeHexColor(c.name)
                                            ? getColorDisplayName(
                                                c.name,
                                                newProduct.colors
                                              )
                                            : c.name}
                                        </span>
                                        {normalizeHexColor(c.name) && (
                                          <span className="image-variant-bind__suggestion-hex">
                                            {c.name}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                            <small className="image-variant-bind__hint">
                              Start typing — matching names appear. Includes extra colors for search; hex like #FF0000
                              works on blur.
                            </small>
                            <div className="image-variant-bind__hex">
                              <input
                                type="color"
                                value={hexDraftByImage[index] || '#888888'}
                                onChange={(e) =>
                                  setHexDraftByImage((prev) => ({
                                    ...prev,
                                    [index]: e.target.value,
                                  }))
                                }
                                aria-label="Hex for this photo"
                              />
                              <button
                                type="button"
                                className="btn-tiny-hex"
                                onClick={() => applyHexToImage(index)}
                              >
                                Link hex
                              </button>
                              <button
                                type="button"
                                className="btn-tiny-eyedropper"
                                onClick={() => handleImageColorPickButton(index)}
                                title={
                                  prefersPhotoColorPicker()
                                    ? 'Tap photo to sample color (mobile-friendly)'
                                    : 'Pick from screen (Chrome / Edge)'
                                }
                              >
                                <i className="fas fa-eye-dropper" aria-hidden="true" />
                                {prefersPhotoColorPicker() ? (
                                  <span className="btn-tiny-eyedropper__label">Photo</span>
                                ) : null}
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              )}

              {(!useProductWizard || wizardStep === 2) && (
              <div className="vih-card vih-card--variant-colors">
                <h4 className="vih-card__title">
                  <i className="fas fa-palette" aria-hidden="true" /> Variant colors
                </h4>
                <p className="vih-card__hint">
                  Add colors for this product here or when you link a photo above. Remove a color with the trash icon
                  (keep at least one).
                </p>
                <div className="vih-quick-add-color-typeahead">
                  <label htmlFor="global-quick-color" className="vih-variant-colors-list__label">
                    Add color
                  </label>
                  <div className="image-variant-bind__typeahead">
                    <input
                      id="global-quick-color"
                      type="text"
                      className="image-variant-bind__input"
                      placeholder="Type color name or hex…"
                      autoComplete="off"
                      spellCheck={false}
                      value={globalQuickColorInput}
                      onChange={(e) => {
                        setGlobalQuickColorInput(e.target.value);
                        setOpenGlobalQuickColorSuggest(true);
                      }}
                      onFocus={() => {
                        if (globalQuickColorBlurTimerRef.current) {
                          clearTimeout(globalQuickColorBlurTimerRef.current);
                          globalQuickColorBlurTimerRef.current = null;
                        }
                        setOpenGlobalQuickColorSuggest(true);
                      }}
                      onBlur={() => {
                        globalQuickColorBlurTimerRef.current = setTimeout(() => {
                          setOpenGlobalQuickColorSuggest(false);
                          commitGlobalQuickColorFromInput();
                          globalQuickColorBlurTimerRef.current = null;
                        }, 200);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitGlobalQuickColorFromInput();
                        }
                      }}
                    />
                    {openGlobalQuickColorSuggest &&
                      filterColorsByQuery(
                        searchableColorsForImageLink,
                        globalQuickColorInput
                      ).length > 0 && (
                        <ul className="image-variant-bind__suggestions">
                          {filterColorsByQuery(
                            searchableColorsForImageLink,
                            globalQuickColorInput
                          ).map((c) => (
                            <li
                              key={`global-q-${c.name}`}
                              className="image-variant-bind__suggestion"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickGlobalQuickColorSuggestion(c.name)}
                            >
                              <span
                                className="image-variant-bind__swatch"
                                style={{ backgroundColor: c.code }}
                              />
                              <span className="image-variant-bind__suggestion-name">
                                {normalizeHexColor(c.name)
                                  ? getColorDisplayName(c.name, newProduct.colors)
                                  : c.name}
                              </span>
                              {normalizeHexColor(c.name) && (
                                <span className="image-variant-bind__suggestion-hex">
                                  {c.name}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                  <small className="image-variant-bind__hint">
                    Choose from suggestions or enter hex like #FF0000 on blur or Enter.
                  </small>
                </div>
                <div className="color-picker-tools color-picker-tools--compact vih-global-hex">
                  <span className="color-picker-tools-label">Add from hex</span>
                  <div className="color-picker-tools-row">
                    <div className="color-picker-native-wrap">
                      <span
                        className="color-picker-native-swatch"
                        style={{ backgroundColor: globalHexDraft || '#888888' }}
                        aria-hidden
                      />
                      <input
                        type="color"
                        value={globalHexDraft || '#888888'}
                        onChange={(e) => setGlobalHexDraft(e.target.value)}
                        aria-label="Pick hex color to add"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-color-add-hex"
                      onClick={applyGlobalHexToProduct}
                    >
                      Add color
                    </button>
                    <button
                      type="button"
                      className="btn-color-eyedropper"
                      onClick={handleGlobalColorPickButton}
                      title={
                        prefersPhotoColorPicker()
                          ? 'Sample a color from your product photos'
                          : 'Pick from screen (Chrome / Edge)'
                      }
                    >
                      <i className="fas fa-eye-dropper" aria-hidden="true" />
                      {prefersPhotoColorPicker() ? 'Pick from photo' : 'Eyedropper'}
                    </button>
                  </div>
                </div>
                {newProduct.colors.length > 0 && (
                  <div className="vih-variant-colors-list" aria-label="Variant colors on this product">
                    <span className="vih-variant-colors-list__label">Colors on this product</span>
                    <div className="vih-variant-color-tags">
                      {newProduct.colors.map((color) => (
                        <span key={`step3-${color}`} className="vih-variant-color-tag">
                          <span
                            className="vih-variant-color-tag__dot"
                            style={{ backgroundColor: getColorCode(color) }}
                            aria-hidden
                          />
                          <span className="vih-variant-color-tag__name" title={String(color)}>
                            {getColorDisplayName(color, newProduct.colors)}
                          </span>
                          <button
                            type="button"
                            className="vih-variant-color-tag__delete"
                            onClick={() => removeVariantColor(color)}
                            disabled={newProduct.colors.length <= 1}
                            title={
                              newProduct.colors.length <= 1
                                ? 'Add another color before removing this one'
                                : 'Remove this color from the product'
                            }
                          >
                            <i className="fas fa-trash-alt" aria-hidden />
                            <span className="sr-only">Delete color</span>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}

              {useProductWizard && wizardStep === 5 && newProduct.colors.length > 0 && (
                <div className="vih-variant-colors-list" aria-label="Variant colors on this product">
                  <span className="vih-variant-colors-list__label">Colors on this product</span>
                  <div className="vih-variant-color-tags">
                    {newProduct.colors.map((color) => (
                      <span key={color} className="vih-variant-color-tag">
                        <span
                          className="vih-variant-color-tag__dot"
                          style={{ backgroundColor: getColorCode(color) }}
                          aria-hidden
                        />
                        <span className="vih-variant-color-tag__name" title={String(color)}>
                          {getColorDisplayName(color, newProduct.colors)}
                        </span>
                        <button
                          type="button"
                          className="vih-variant-color-tag__delete"
                          onClick={() => removeVariantColor(color)}
                          disabled={newProduct.colors.length <= 1}
                          title={
                            newProduct.colors.length <= 1
                              ? 'Add another color before removing this one'
                              : 'Remove this color from the product'
                          }
                        >
                          <i className="fas fa-trash-alt" aria-hidden />
                          <span className="sr-only">Delete color</span>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {useProductWizard && wizardStep === 2 && (
              <div className="vih-card vih-card--video">
                <h4 className="vih-card__title">
                  <i className="fas fa-film" aria-hidden="true" /> Product video (optional)
                </h4>
                <p className="vih-card__hint">Short showcase clip on the product page. Max 5MB — MP4, WebM, or MOV.</p>
                <div className="image-upload-area">
                  <input
                    ref={productVideoInputRef}
                    type="file"
                    id="product-video-upload-wizard"
                    accept={PRODUCT_VIDEO_ACCEPT}
                    onChange={handleProductVideoChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="product-video-upload-wizard" className="upload-btn">
                    <i className="fas fa-film" />
                    <span>Select video</span>
                    <small>Up to 5MB · Shown on the storefront product page with images</small>
                  </label>
                </div>
                {(pendingVideo || existingVideoUrl) && (
                  <div className="uploaded-images" style={{ marginTop: 16 }}>
                    <div className="image-preview" style={{ maxWidth: 360 }}>
                      <video
                        key={pendingVideo?.previewUrl || existingVideoUrl}
                        src={pendingVideo?.previewUrl || existingVideoUrl}
                        controls
                        playsInline
                        style={{ width: '100%', maxHeight: 220, borderRadius: 8, background: '#000' }}
                      />
                      <div className="image-info">
                        <span className="image-name">
                          {pendingVideo?.name || 'Current video'}
                        </span>
                        {pendingVideo?.size != null && (
                          <span className="image-size">
                            {(pendingVideo.size / 1024).toFixed(1)} KB
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="remove-image"
                        onClick={removeProductVideo}
                        title="Remove video"
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}

              {(!useProductWizard || wizardStep === 5) && (
              <div className="vih-card vih-card--stock">
                <h4 className="vih-card__title">
                  <i className="fas fa-boxes" aria-hidden="true" /> Inventory
                </h4>
                <p className="vih-card__hint vih-card__hint--stock">
                  Pick sizes (optional), then enter quantities. At least one cell must total to 1 or more across the
                  product. Colors come from each photo&apos;s variant field, quick-add search above, or hex.
                </p>

                <div className="vih-inventory-sizes">
                  <span className="vih-inventory-sizes__label">Sizes (optional)</span>
                  <p className="vih-inventory-sizes__hint">
                    {newProduct.mainCategory
                      ? `Sizes match your main category (“${newProduct.mainCategory}”). Pick the options you carry. Leave all off for a single total or color-only rows.`
                      : 'Select a main category above for kids / shoe size guides, or use standard S–XXL. Leave all off for a single total or color-only rows.'}
                  </p>
                  <div className="checkbox-group vih-inventory-sizes__chips">
                    {sizePresetOptions.map((size) => (
                      <label key={size} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newProduct.sizes.includes(size)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleArrayChange('sizes', [...newProduct.sizes, size]);
                            } else {
                              handleArrayChange(
                                'sizes',
                                newProduct.sizes.filter((s) => s !== size)
                              );
                            }
                          }}
                        />
                        <span>{size}</span>
                      </label>
                    ))}
                  </div>
                </div>

          {isSimpleStockMode && (
            <div className="vih-stock-block">
              <div className="vih-stock-block__head">
                <h5 className="vih-stock-block__title">Total quantity (no variants)</h5>
                <p className="vih-stock-block__desc">Single SKU — set one number (minimum 1 to save).</p>
                <div className="stock-summary">
                  <span className="total-stock">
                    Total stock: <strong>{getTotalStock()}</strong>
                  </span>
                </div>
              </div>
              <div className="vih-stock-block__body">
                <div className="form-group">
                  <label htmlFor="simple-stock-qty">Quantity in stock *</label>
                  <input
                    id="simple-stock-qty"
                    type="number"
                    min={1}
                    step={1}
                    className="stock-input"
                    style={{ maxWidth: '200px' }}
                    value={(() => {
                      const v = newProduct.stock?.[SIMPLE_STOCK_COLOR]?.[SIMPLE_STOCK_SIZE];
                      if (v === undefined || v === '' || v === 0) return '';
                      return v;
                    })()}
                    onChange={(e) =>
                      updateStock(SIMPLE_STOCK_COLOR, SIMPLE_STOCK_SIZE, e.target.value)
                    }
                    onFocus={(e) => {
                      suppressStockPlaceholder('simple-qty');
                      const cur = newProduct.stock[SIMPLE_STOCK_COLOR]?.[SIMPLE_STOCK_SIZE];
                      if (cur === 0 || cur === '0') {
                        e.target.value = '';
                        updateStock(SIMPLE_STOCK_COLOR, SIMPLE_STOCK_SIZE, '');
                      }
                    }}
                    onBlur={(e) => {
                      restoreStockPlaceholder('simple-qty');
                      if (e.target.value === '' || e.target.value === null) {
                        updateStock(SIMPLE_STOCK_COLOR, SIMPLE_STOCK_SIZE, 0);
                      }
                    }}
                    placeholder={stockQtyPlaceholder('simple-qty', 'Minimum 1')}
                  />
                </div>
              </div>
            </div>
          )}

          {isColorOnlyStockMode && (
            <div className="vih-stock-block">
              <div className="vih-stock-block__head">
                <h5 className="vih-stock-block__title">Stock by color</h5>
                <p className="vih-stock-block__desc">
                  One size slot per color on the server. Total must be at least 1.
                </p>
                <div className="stock-summary">
                  <span className="total-stock">
                    Total stock: <strong>{getTotalStock()}</strong>
                  </span>
                  {getTotalStock() === 0 && (
                    <span
                      className="stock-warning"
                      style={{
                        color: '#dc3545',
                        fontSize: '12px',
                        marginLeft: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      ⚠️ Set stock &gt; 0 for at least one color
                    </span>
                  )}
                </div>
              </div>
              <div className="vih-stock-block__body">
                <div className="stock-table-container">
                  <table className="stock-table stock-table--color-only">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newProduct.colors.map((color) => {
                        const colorStockPhKey = `color-qty-${color}`;
                        return (
                        <tr key={color}>
                          <td className="color-cell">
                            <div className="color-indicator">
                              <div
                                className="color-dot"
                                style={{
                                  backgroundColor: getColorCode(color),
                                }}
                              />
                              <span
                                title={normalizeHexColor(String(color)) ? String(color) : undefined}
                              >
                                {getColorDisplayName(color, newProduct.colors)}
                              </span>
                              {newProduct.colors.length > 1 && (
                                <button
                                  type="button"
                                  className="vih-remove-color-btn"
                                  onClick={() => removeVariantColor(color)}
                                  title="Delete this color"
                                >
                                  <i className="fas fa-trash-alt" aria-hidden />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="stock-input-cell">
                            <input
                              type="number"
                              min="0"
                              value={
                                newProduct.stock[color]?.[SIMPLE_STOCK_SIZE] === 0 ||
                                newProduct.stock[color]?.[SIMPLE_STOCK_SIZE] === ''
                                  ? ''
                                  : newProduct.stock[color]?.[SIMPLE_STOCK_SIZE] || ''
                              }
                              onChange={(e) => updateStock(color, SIMPLE_STOCK_SIZE, e.target.value)}
                              onFocus={(e) => {
                                suppressStockPlaceholder(colorStockPhKey);
                                const currentValue = newProduct.stock[color]?.[SIMPLE_STOCK_SIZE];
                                if (currentValue === 0 || currentValue === '0') {
                                  e.target.value = '';
                                  updateStock(color, SIMPLE_STOCK_SIZE, '');
                                }
                              }}
                              onBlur={(e) => {
                                restoreStockPlaceholder(colorStockPhKey);
                                if (e.target.value === '' || e.target.value === null) {
                                  updateStock(color, SIMPLE_STOCK_SIZE, 0);
                                }
                              }}
                              className="stock-input"
                              placeholder={stockQtyPlaceholder(colorStockPhKey, '0')}
                            />
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {isSizeOnlyStockMode && (
            <div className="vih-stock-block">
              <div className="vih-stock-block__head">
                <h5 className="vih-stock-block__title">Stock by size</h5>
                <p className="vih-stock-block__desc">
                  One color slot for all sizes on the server. Total must be at least 1.
                </p>
                <div className="stock-summary">
                  <span className="total-stock">
                    Total stock: <strong>{getTotalStock()}</strong>
                  </span>
                  {getTotalStock() === 0 && (
                    <span
                      className="stock-warning"
                      style={{
                        color: '#dc3545',
                        fontSize: '12px',
                        marginLeft: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      ⚠️ Set stock &gt; 0 for at least one size
                    </span>
                  )}
                </div>
              </div>
              <div className="vih-stock-block__body">
                <div className="stock-table-container">
                  <table className="stock-table stock-table--size-only">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newProduct.sizes.map((size) => {
                        const sizeStockPhKey = `size-qty-${size}`;
                        return (
                        <tr key={size}>
                          <td>{size}</td>
                          <td className="stock-input-cell">
                            <input
                              type="number"
                              min="0"
                              value={
                                newProduct.stock[SIMPLE_STOCK_COLOR]?.[size] === 0 ||
                                newProduct.stock[SIMPLE_STOCK_COLOR]?.[size] === ''
                                  ? ''
                                  : newProduct.stock[SIMPLE_STOCK_COLOR]?.[size] || ''
                              }
                              onChange={(e) =>
                                updateStock(SIMPLE_STOCK_COLOR, size, e.target.value)
                              }
                              onFocus={(e) => {
                                suppressStockPlaceholder(sizeStockPhKey);
                                const currentValue =
                                  newProduct.stock[SIMPLE_STOCK_COLOR]?.[size];
                                if (currentValue === 0 || currentValue === '0') {
                                  e.target.value = '';
                                  updateStock(SIMPLE_STOCK_COLOR, size, '');
                                }
                              }}
                              onBlur={(e) => {
                                restoreStockPlaceholder(sizeStockPhKey);
                                if (e.target.value === '' || e.target.value === null) {
                                  updateStock(SIMPLE_STOCK_COLOR, size, 0);
                                }
                              }}
                              className="stock-input"
                              placeholder={stockQtyPlaceholder(sizeStockPhKey, '0')}
                            />
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {isMatrixStockMode && (
            <div className="vih-stock-block">
              <div className="vih-stock-block__head">
                <h5 className="vih-stock-block__title">Color × size grid</h5>
                <p className="vih-stock-block__desc">Set quantity for each combination.</p>
                <div className="stock-summary">
                  <span className="total-stock">
                    Total stock: <strong>{getTotalStock()}</strong>
                  </span>
                  {getTotalStock() === 0 && (
                    <span
                      className="stock-warning"
                      style={{
                        color: '#dc3545',
                        fontSize: '12px',
                        marginLeft: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      ⚠️ Set stock quantity &gt; 0 for at least one combination
                    </span>
                  )}
                </div>
              </div>
              <div className="vih-stock-block__body">
                <div className="stock-table-container">
                  <table className="stock-table">
                    <thead>
                      <tr>
                        <th>Color</th>
                        {newProduct.sizes.map((size) => (
                          <th key={size}>{size}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newProduct.colors.map((color) => (
                        <tr key={color}>
                          <td className="color-cell">
                            <div className="color-indicator">
                              <div
                                className="color-dot"
                                style={{
                                  backgroundColor: getColorCode(color),
                                }}
                              />
                              <span title={normalizeHexColor(String(color)) ? String(color) : undefined}>
                                {getColorDisplayName(color, newProduct.colors)}
                              </span>
                              {newProduct.colors.length > 1 && (
                                <button
                                  type="button"
                                  className="vih-remove-color-btn"
                                  onClick={() => removeVariantColor(color)}
                                  title="Delete this color"
                                >
                                  <i className="fas fa-trash-alt" aria-hidden />
                                </button>
                              )}
                            </div>
                          </td>
                          {newProduct.sizes.map((size) => {
                            const matrixStockPhKey = `matrix-qty-${color}-${size}`;
                            return (
                            <td key={size} className="stock-input-cell">
                              <input
                                type="number"
                                min="0"
                                value={
                                  newProduct.stock[color]?.[size] === 0 ||
                                  newProduct.stock[color]?.[size] === ''
                                    ? ''
                                    : newProduct.stock[color]?.[size] || ''
                                }
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  updateStock(color, size, inputValue);
                                }}
                                onFocus={(e) => {
                                  suppressStockPlaceholder(matrixStockPhKey);
                                  const currentValue = newProduct.stock[color]?.[size];
                                  if (currentValue === 0 || currentValue === '0') {
                                    e.target.value = '';
                                    updateStock(color, size, '');
                                  }
                                }}
                                onBlur={(e) => {
                                  restoreStockPlaceholder(matrixStockPhKey);
                                  if (e.target.value === '' || e.target.value === null) {
                                    updateStock(color, size, 0);
                                  }
                                }}
                                className="stock-input"
                                placeholder={stockQtyPlaceholder(matrixStockPhKey, '0')}
                              />
                            </td>
                          );})}
                          <td className="color-total">
                            <strong>{getColorStock(color)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
              </div>
              )}

            </div>
          </div>
          )}

          {/* Step 5 — Product status (wizard last step) */}
          {(!useProductWizard || wizardStep === 5) && (
          <>
          {!useProductWizard && (
          <div className="form-section">
            <div className="section-header">
              <h3>Product video (optional)</h3>
              <p>Short showcase clip next to images on the product page. Max 5MB — MP4, WebM, or MOV.</p>
            </div>
            <div className="section-content">
              <div className="image-upload-area">
                <input
                  ref={productVideoInputRef}
                  type="file"
                  id="product-video-upload"
                  accept={PRODUCT_VIDEO_ACCEPT}
                  onChange={handleProductVideoChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="product-video-upload" className="upload-btn">
                  <i className="fas fa-film" />
                  <span>Select video</span>
                  <small>Up to 5MB · Shown on the storefront product page with images</small>
                </label>
              </div>
              {(pendingVideo || existingVideoUrl) && (
                <div className="uploaded-images" style={{ marginTop: 16 }}>
                  <div className="image-preview" style={{ maxWidth: 360 }}>
                    <video
                      key={pendingVideo?.previewUrl || existingVideoUrl}
                      src={pendingVideo?.previewUrl || existingVideoUrl}
                      controls
                      playsInline
                      style={{ width: '100%', maxHeight: 220, borderRadius: 8, background: '#000' }}
                    />
                    <div className="image-info">
                      <span className="image-name">
                        {pendingVideo?.name || 'Current video'}
                      </span>
                      {pendingVideo?.size != null && (
                        <span className="image-size">
                          {(pendingVideo.size / 1024).toFixed(1)} KB
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="remove-image"
                      onClick={removeProductVideo}
                      title="Remove video"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          <div className="form-section wizard-step-panel">
            <div className="section-header">
              <h3>Product Status</h3>
              <p>Set product visibility and features</p>
            </div>
            <div className="section-content">
              <div className="status-options">
                {allowFeaturedProduct && (
                  <label className="status-item">
                    <input
                      type="checkbox"
                      checked={newProduct.isFeatured}
                      onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                    />
                    <span>Featured Product</span>
                  </label>
                )}
                <label className="status-item">
                  <input
                    type="checkbox"
                    checked={newProduct.isNew}
                    onChange={(e) => handleInputChange('isNew', e.target.checked)}
                  />
                  <span>New Arrival</span>
                </label>
                <label className="status-item">
                  <input
                    type="checkbox"
                    checked={newProduct.isOnSale}
                    onChange={(e) => handleInputChange('isOnSale', e.target.checked)}
                  />
                  <span>On Sale</span>
                </label>
              </div>
            </div>
          </div>
          </>
          )}

          {/* Action Buttons — legacy full form */}
          {!useProductWizard && (
          <div className="form-action-buttons">
            <button 
              type="button"
              className="action-btn add-product-btn"
              onClick={handleAddProduct}
              disabled={creating || updating}
            >
              {(creating || updating) ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {editingProduct ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  <i className={editingProduct ? 'fas fa-save' : 'fas fa-plus'}></i>
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </>
              )}
            </button>
            <button 
              type="button"
              className={`action-btn status-btn ${newProduct.status === 'draft' ? 'draft' : ''}`}
              onClick={() => handleAddProduct('draft')}
              disabled={creating || updating}
            >
              {(creating || updating) ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving Draft...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Draft
                </>
              )}
            </button>
          </div>
          )}
        </div>

        {useProductWizard && (
          <div className="add-product-wizard-footer" role="navigation" aria-label="Product form navigation">
            <button
              type="button"
              className="wizard-footer-btn wizard-footer-btn--back"
              onClick={goWizardBack}
            >
              <i className="fas fa-arrow-left" aria-hidden />
              Back
            </button>
            <button
              type="button"
              className="wizard-footer-btn wizard-footer-btn--draft"
              onClick={saveWizardDraft}
              disabled={creating || updating}
            >
              {(creating || updating) ? (
                <i className="fas fa-spinner fa-spin" aria-hidden />
              ) : (
                <i className="fas fa-save" aria-hidden />
              )}
              Save draft
            </button>
            {showWizardNext && (
              <button
                type="button"
                className={`wizard-footer-btn wizard-footer-btn--next${wizardStepComplete ? ' is-ready' : ''}`}
                onClick={goWizardNext}
                disabled={creating || updating}
                aria-describedby={wizardStepComplete ? undefined : 'wizard-step-incomplete-hint'}
              >
                {isLastWizardStep ? (
                  <>
                    <i className={editingProduct ? 'fas fa-save' : 'fas fa-check'} aria-hidden />
                    {editingProduct ? 'Update' : 'Publish'}
                  </>
                ) : (
                  <>
                    Next
                    <i className="fas fa-arrow-right" aria-hidden />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default AddNewProduct;
